import ThemeToggle from './components/ThemeToggle';
import { AccountAvatar } from './components/PageHeading';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import SavedFilterHeader from './components/SavedFilterHeader';
import FilterGroups from './components/FilterGroups';
import { ArticleGrid, ArticleImage, ArticleViewPicker, useArticleView } from './components/ArticleView';
import KeywordSearchInput from './components/KeywordSearchInput';
import { CompactSearchOptions, ControlIcon, SearchOptionsHelp } from './components/CompactControls';
import { notificationApi } from './utils/newsNotifications';
import { assetUrl } from './utils/assetUrl';
import BannerImage from './components/BannerImage';
import { useNavigate } from 'react-router-dom';
import Datetime from 'react-datetime';
import moment from 'moment';
import './Search.css';
import './components/BannerStyles.css';
import 'react-datetime/css/react-datetime.css';
import logo from './logo.png';
import {
  clearAccountSession,
  readLoginCredentials,
  clearArticleCacheOnLogin,
  getArticleCacheBuster,
  hasCurrentFilter,
  storeCurrentFilter
} from './utils/sessionCache';
import { trackEvent } from './utils/analytics';
import useInterestPrompt from './hooks/useInterestPrompt';
import useFilterDismissal from './hooks/useFilterDismissal';
import {
  buildCategoryTags,
  QUICK_TOPIC_KEY
} from './utils/quickPresets';
import {
  createShareableFilterUrl,
  hasSharedFilter,
  readSharedFilter,
  removeSharedFilterFromUrl
} from './utils/filterShare';

const tagIsCategory = (t, name) =>
  t && typeof t === 'object' && t.type === 'category' && t.name === name;

const tagIsSubcategory = (t, name, parent) =>
  t && typeof t === 'object' && t.type === 'subcategory' && t.name === name && t.parent === parent;

const hasCategoryTag = (tags, name) => tags.some(t => tagIsCategory(t, name));

const hasSubcategoryTag = (tags, name, parent) => tags.some(t => tagIsSubcategory(t, name, parent));

const categoryHasSubcategories = (categories, categoryName) =>
  Array.isArray(categories?.[categoryName]) && categories[categoryName].length > 0;

const isKnownEmptyCategory = (categories, categoryName) =>
  categories &&
  typeof categories === 'object' &&
  Object.prototype.hasOwnProperty.call(categories, categoryName) &&
  !categoryHasSubcategories(categories, categoryName);

const removeEmptyCategorySelections = (tags, categories) => {
  if (!Array.isArray(tags) || !categories || typeof categories !== 'object') return tags;

  let changed = false;
  const filteredTags = tags.filter(tag => {
    if (!tag || typeof tag !== 'object') return true;

    if (tag.type === 'category' && isKnownEmptyCategory(categories, tag.name)) {
      changed = true;
      return false;
    }

    if (tag.type === 'subcategory' && tag.parent && isKnownEmptyCategory(categories, tag.parent)) {
      changed = true;
      return false;
    }

    return true;
  });

  return changed ? filteredTags : tags;
};

const dedupeTags = (tags) => {
  const seen = new Set();
  return tags.filter(t => {
    if (!t || typeof t !== 'object') return true;
    const key = `${t.type}:${t.name}:${t.parent || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const migrateTagList = (oldTags, categories) => {
  if (!Array.isArray(oldTags)) return [];
  const result = [];
  for (const item of oldTags) {
    if (item && typeof item === 'object' && item.type) {
      result.push(item);
      continue;
    }
    if (typeof item !== 'string') continue;
    const tag = item;
    let added = false;
    if (categories && categories[tag]) {
      result.push({ type: 'category', name: tag });
      added = true;
    }
    if (categories) {
      for (const [cat, subs] of Object.entries(categories)) {
        if (Array.isArray(subs) && subs.includes(tag)) {
          result.push({ type: 'subcategory', name: tag, parent: cat });
          added = true;
        }
      }
    }
    if (!added) {
      result.push({ type: 'subcategory', name: tag, parent: null });
    }
  }
  return dedupeTags(result);
};

const normalizeSearchText = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/đ/g, 'd')
  .replace(/Đ/g, 'D')
  .toLowerCase();


function Search({ active = true }) {
  // Search i filter stanja
  const [query, setQuery] = useState('');
  const [multiKeywordEnabled, setMultiKeywordEnabled] = useState(false);
  const [multiKeyword, setMultiKeyword] = useState(true);
  const [keywordMode, setKeywordMode] = useState('any');
  const [searchAll, setSearchAll] = useState(false);
  const [searchAllEnabled, setSearchAllEnabled] = useState(false);
  const keywordInputRef = useRef(null);
  const [appliedSearchOptions, setAppliedSearchOptions] = useState({});
  const restoreSearchOptions = useCallback(filter => {
    setMultiKeyword(filter?.multi_keyword !== false);
    setKeywordMode(filter?.keyword_mode === 'all' ? 'all' : 'any');
    setSearchAll(filter?.search_all === true);
  }, []);
  const [appliedQuery, setAppliedQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  useFilterDismissal(showFilters, setShowFilters);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState({});
  const [portals, setPortals] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPortals, setSelectedPortals] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [includeTags, setIncludeTags] = useState(true);
  const [includePortals, setIncludePortals] = useState(true);
  const [showClearAllConfirmation, setShowClearAllConfirmation] = useState(false);
  const [commercialBanner, setCommercialBanner] = useState('');
  const [commercialBanners, setCommercialBanners] = useState([]);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [commercialDimensions, setCommercialDimensions] = useState({
    width: 1200,
    height: 120,
    displayWidth: 1200,
    displayHeight: 120
  });
  const [error, setError] = useState(null);

  // Pagination i stanje učitavanja
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [articleView, setArticleView] = useArticleView();

  // Ref za praćenje toggle stanja u popstate handleru (izbjegava stale closure)
  const toggleStatesRef = useRef({
    showFilters: false,
    showMobileMenu: false,
    showFiltersDropdown: false,
    showAccountDropdown: false,
    expandedGroups: {}
  });

  // Scroll to top button
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lazy migracija starog string-formata selectedTags u composite objekte
  // (potrebno za saved filters spremljene prije refactora)
  useEffect(() => {
    if (Object.keys(categories).length === 0) return;
    if (!selectedTags.some(t => typeof t === 'string' || (t && typeof t === 'object' && !t.type))) return;
    setSelectedTags(prev => migrateTagList(prev, categories));
  }, [categories, selectedTags]);

  useEffect(() => {
    if (Object.keys(categories).length === 0 || selectedTags.length === 0) return;
    setSelectedTags(prev => removeEmptyCategorySelections(prev, categories));
  }, [categories, selectedTags]);


  const observer = useRef();
  const lastArticleRef = useRef(null);
  const observeLastArticle = useCallback((el) => {
    const prevRef = lastArticleRef.current;
    if (prevRef && observer.current && prevRef !== el) {
      observer.current.unobserve(prevRef);
    }
    lastArticleRef.current = el;
    if (el && observer.current && el !== prevRef) {
      observer.current.observe(el);
    }
  }, []);
  // Keep latest fetchResults for the IntersectionObserver callback (avoids stale closure)
  const fetchResultsRef = useRef(null);
  const activeSearchRef = useRef({ requestId: 0, controller: null });
  const searchRequestInFlightRef = useRef(false);
  const appliedSearchPayloadRef = useRef(null);
  const skipNextFilterHighlightRef = useRef(false);
  const initialFilterForSearchRef = useRef(null);
  const sharedFilterPresentRef = useRef(hasSharedFilter(window.location.search));
  // Refs for mobile filter toggle: position panel under the sticky bar at user's current scroll
  const filtersRef = useRef(null);
  const mobileStickyRef = useRef(null);
  const resultsHeadingRef = useRef(null);
  const limit = 12;

  useEffect(() => () => {
    activeSearchRef.current.controller?.abort();
  }, []);
  
  // Refs to track if data has been fetched
  const countriesFetchedRef = useRef(false);
  const portalsFetchedRef = useRef(false);
  const favoriteFiltersFetchedRef = useRef(false);

  // Dropdown i favorite filter stanja
  const [savedFilters, setSavedFilters] = useState([]);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false); // Add new state for About modal
  const [editingFilterIndex, setEditingFilterIndex] = useState(null); // Track which filter is being edited
  const [editingFilterName, setEditingFilterName] = useState(''); // Temporary value for editing
  const [activeFilterName, setActiveFilterName] = useState(null); // Track currently active filter
  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false); // Confirmation before updating filter
  const [isCreatingNewFilter, setIsCreatingNewFilter] = useState(false); // Track if we're creating a new (unsaved) filter
  const [newFilterName, setNewFilterName] = useState(''); // Name input for new filter
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareFeedback, setShareFeedback] = useState('');

  // Prijava i tema stanja
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem("token"));
  const [darkTheme, setDarkTheme] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme ? savedTheme === "true" : true; // Default to dark theme if no saved preference
  });

  // Add new state for expanded groups
  const [expandedGroups, setExpandedGroups] = useState({});

  // Add new state for time input focus
  const [timeInputFocused, setTimeInputFocused] = useState(false);

  // Update isAdmin state initialization
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem("isAdmin") === "true");
  const [username, setUsername] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.username || '';
    } catch { return ''; }
  });

  // Subcategory filters are off by default, while article labels are visible to everyone.
  const [showSubcategories, setShowSubcategories] = useState(false);
  const [showSubcategoryOnArticles, setShowSubcategoryOnArticles] = useState(true);
  const [showPortalOnSubcategories, setShowPortalOnSubcategories] = useState(true);
  // Subcategory -> [portal_name, ...] map (from /initial-data)
  const [tagPortalsMap, setTagPortalsMap] = useState({});

  // Maintenance mode
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Add new state to track if initial filter is loaded
  const [initialFilterLoaded, setInitialFilterLoaded] = useState(false);

  // Add new state to track if tags are loaded
  const [tagsLoaded, setTagsLoaded] = useState(false);

  // Add new state for tracking loading sequence
  const [loadingSequence, setLoadingSequence] = useState({
    categoriesLoaded: false,
    tagsLoaded: false,
    countriesLoaded: false,
    portalsLoaded: false,
    favoriteFiltersLoaded: false,
    filterLoaded: false,
    commercialBannerLoaded: false,
    firstArticlesLoaded: false,
    firstArticleImagesLoaded: false
  });

  // Add new state to track if we're in the initial loading phase
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [isSearching, setIsSearching] = useState(false); // true between search start and results received

  const hasActiveFilters = useMemo(() => (
    selectedTags.length > 0 ||
    selectedPortals.length > 0 ||
    selectedCountries.length > 0 ||
    Boolean(fromDate) ||
    Boolean(toDate) ||
    !includeTags ||
    !includePortals ||
    Boolean(query.trim())
  ), [selectedTags, selectedPortals, selectedCountries, fromDate, toDate, includeTags, includePortals, query]);

  const buildFilterState = useCallback((filter, categoryMap = {}) => ({
    includeTags: filter?.include_tags !== undefined ? filter.include_tags : true,
    selectedTags: migrateTagList(filter?.selected_tags || [], categoryMap),
    fromDate: filter?.from_date ? moment(filter.from_date, 'YYYY-MM-DDTHH:mm') : null,
    toDate: filter?.to_date ? moment(filter.to_date, 'YYYY-MM-DDTHH:mm') : null,
    includePortals: filter?.include_portals !== undefined ? filter.include_portals : true,
    selectedPortals: Array.isArray(filter?.selected_portals) ? filter.selected_portals : [],
    selectedCountries: Array.isArray(filter?.selected_countries) ? filter.selected_countries : [],
    multiKeyword: filter?.multi_keyword !== false,
    keywordMode: filter?.keyword_mode === 'all' ? 'all' : 'any',
    searchAll: filter?.search_all === true,
    query: filter?.query || '',
    name: filter?.name || null
  }), []);

  const applyFilterState = useCallback((filterState) => {
    setIncludeTags(filterState.includeTags);
    setSelectedTags(filterState.selectedTags);
    setFromDate(filterState.fromDate);
    setToDate(filterState.toDate);
    setIncludePortals(filterState.includePortals);
    setSelectedPortals(filterState.selectedPortals);
    setSelectedCountries(filterState.selectedCountries);
    setMultiKeyword(filterState.multiKeyword);
    setKeywordMode(filterState.keywordMode);
    setSearchAll(filterState.searchAll);
    setQuery(filterState.query);
    setAppliedQuery(filterState.query);
    setActiveFilterName(filterState.name);
  }, []);
  
  // UI is ready when structural data is loaded (no need to wait for articles)
  // This unblocks the loading overlay early → improves LCP
  const uiReady = loadingSequence.categoriesLoaded &&
                  loadingSequence.tagsLoaded &&
                  loadingSequence.countriesLoaded &&
                  loadingSequence.portalsLoaded &&
                  loadingSequence.favoriteFiltersLoaded &&
                  loadingSequence.filterLoaded &&
                  loadingSequence.commercialBannerLoaded;

  // All data loaded = UI ready + articles fetched (used to show search results)
  const allDataLoaded = uiReady && loadingSequence.firstArticlesLoaded;
  
  // Add global timeout to prevent infinite loading
  useEffect(() => {
    // Force uiReady after 4s (overlay must not block LCP indefinitely)
    const uiTimeout = setTimeout(() => {
      setLoadingSequence(prev => ({
        ...prev,
        categoriesLoaded: true,
        tagsLoaded: true,
        countriesLoaded: true,
        portalsLoaded: true,
        favoriteFiltersLoaded: true,
        filterLoaded: true,
        commercialBannerLoaded: true,
      }));
    }, 4000);

    // Force all data after 10s (articles must eventually show)
    const globalTimeout = setTimeout(() => {
      setLoadingSequence(prev => ({
        ...prev,
        categoriesLoaded: true,
        tagsLoaded: true,
        countriesLoaded: true,
        portalsLoaded: true,
        favoriteFiltersLoaded: true,
        filterLoaded: true,
        commercialBannerLoaded: true,
        firstArticlesLoaded: true
      }));
    }, 10000);

    return () => {
      clearTimeout(uiTimeout);
      clearTimeout(globalTimeout);
    };
  }, []);

  // Add new state for mobile menu
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileUserSubmenu, setShowMobileUserSubmenu] = useState(false);
  const [commercialBannerLink, setCommercialBannerLink] = useState('');
  useEffect(() => {
    if (!commercialBanners.length) return;
    const banner = commercialBanners[activeBannerIndex % commercialBanners.length];
    setCommercialBanner(banner.url);
    setCommercialBannerLink(banner.link || '');
    if (banner.dimensions) setCommercialDimensions({
      ...banner.dimensions,
      displayWidth: banner.dimensions.displayWidth || '100%'
    });
    if (commercialBanners.length < 2) return;
    const nextBanner = commercialBanners[(activeBannerIndex + 1) % commercialBanners.length];
    const nextImage = new Image();
    nextImage.src = assetUrl(nextBanner.url);
    const timer = window.setTimeout(() => {
      setActiveBannerIndex(index => (index + 1) % commercialBanners.length);
    }, Math.max(1, banner.durationSeconds || 5) * 1000);
    return () => window.clearTimeout(timer);
  }, [commercialBanners, activeBannerIndex]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [showInterestPrompt, setShowInterestPrompt] = useInterestPrompt({
    loggedIn,
    uiReady: uiReady && active,
    sharedFilterPresent: sharedFilterPresentRef.current
  });

  // Add new state for login dropdown
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const [loginFormData, setLoginFormData] = useState({
    identifier: "",
    password: "",
  });
  const [loginMessage, setLoginMessage] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [hasUserClosedLoginDropdown, setHasUserClosedLoginDropdown] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const loginBarRef = useRef(null);
  const [loginBarHeight, setLoginBarHeight] = useState(46);
  const loginIdentifierInputRef = useRef(null);
  const loginPasswordInputRef = useRef(null);
  const latestLoginFormDataRef = useRef(loginFormData);
  const capturedLoginFormDataRef = useRef(null);

  const readLoginFormData = useCallback((fallback = latestLoginFormDataRef.current) =>
    readLoginCredentials(loginIdentifierInputRef.current, loginPasswordInputRef.current, fallback), []);

  const captureLoginFormData = useCallback(() => {
    const credentials = readLoginFormData();
    capturedLoginFormDataRef.current = credentials;
    latestLoginFormDataRef.current = credentials;
    setLoginFormData(credentials);
    return credentials;
  }, [readLoginFormData]);

  // Visual highlight on Pretrazi button when filters change but search hasn't refreshed yet
  const [searchHighlight, setSearchHighlight] = useState(false);
  const searchHasPendingQuery = query.trim() !== appliedQuery.trim();

  const navigate = useNavigate();

  // API base URL — relative path so it works behind any reverse proxy (local nginx, jaosve.com, etc.)
  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
  const [notificationSubscriptions, setNotificationSubscriptions] = useState([]);
  const refreshNotificationSubscriptions = useCallback(async () => {
    if (!localStorage.getItem('token')) { setNotificationSubscriptions([]); return; }
    setNotificationSubscriptions(await notificationApi());
  }, []);
  useEffect(() => {
    if (!loggedIn) { setNotificationSubscriptions([]); return; }
    const refresh = () => refreshNotificationSubscriptions().catch(() => {});
    refresh();
    window.addEventListener('news-notifications-changed', refresh);
    return () => window.removeEventListener('news-notifications-changed', refresh);
  }, [loggedIn, refreshNotificationSubscriptions]);

  const clearQuickTopic = useCallback(() => {
    localStorage.removeItem(QUICK_TOPIC_KEY);
  }, []);

  useEffect(() => {
    localStorage.removeItem(QUICK_TOPIC_KEY);
  }, []);

  const clearSharedFeedContext = useCallback(() => {
    if (!sharedFilterPresentRef.current) return;
    sharedFilterPresentRef.current = false;
    window.history.replaceState(
      window.history.state,
      '',
      removeSharedFilterFromUrl(window.location.href)
    );
  }, []);

  // Sinhronizuj toggle stanja sa refom
  useEffect(() => {
    toggleStatesRef.current = {
      showFilters, showMobileMenu, showFiltersDropdown, showAccountDropdown, expandedGroups,
      showLoginDropdown, showShareDialog, showInterestPrompt, showMobileUserSubmenu
    };
  }, [showFilters, showMobileMenu, showFiltersDropdown, showAccountDropdown, expandedGroups,
    showLoginDropdown, showShareDialog, showInterestPrompt, showMobileUserSubmenu]);

  useEffect(() => {
    if (!showFilters) {
      setExpandedGroups(prev => (
        Object.values(prev).some(Boolean) ? {} : prev
      ));
    }
  }, [showFilters]);

  // Mobile back button: zatvori otvorene toggleove, pa tek onda izađi
  useEffect(() => {
    if (!active) return undefined;
    if (window.jaosveNative) {
      const dismiss = event => {
        const s = toggleStatesRef.current;
        if (!(s.showFilters || s.showMobileMenu || s.showFiltersDropdown || s.showAccountDropdown
          || s.showLoginDropdown || s.showShareDialog || s.showInterestPrompt || s.showMobileUserSubmenu
          || Object.values(s.expandedGroups).some(Boolean))) return;
        event.preventDefault();
        setShowFilters(false);
        setShowMobileMenu(false);
        setShowFiltersDropdown(false);
        setShowAccountDropdown(false);
        setShowLoginDropdown(false);
        setShowShareDialog(false);
        setShowInterestPrompt(false);
        setShowMobileUserSubmenu(false);
        setExpandedGroups({});
      };
      window.addEventListener('jaosve-dismiss-overlays', dismiss);
      return () => window.removeEventListener('jaosve-dismiss-overlays', dismiss);
    }
    const handlePopState = () => {
      const s = toggleStatesRef.current;
      const hasExpandedGroups = Object.values(s.expandedGroups).some(Boolean);
      if (s.showFilters || s.showMobileMenu || s.showFiltersDropdown || s.showAccountDropdown || hasExpandedGroups) {
        setShowFilters(false);
        setShowMobileMenu(false);
        setShowFiltersDropdown(false);
        setShowAccountDropdown(false);
        setExpandedGroups({});
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [active, setShowInterestPrompt]);

  // Start total page load timer on mount
  useEffect(() => {
    console.time('🚀 TOTAL PAGE LOAD TIME');
    return () => {}; // Cleanup
  }, []);

  // Only show images saved locally on our server (/static/ prefix).
  // External URLs are skipped to avoid proxy errors (502) for protected CDNs.
  const getProxiedImageUrl = useCallback((imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('/static/')) {
      return `${API_BASE_URL}${imageUrl}`;
    }
    return null;
  }, []);

  // Extract clean portal name from source URL
  const getPortalName = useCallback((source) => {
    if (!source) return '';
    try {
      const url = new URL(source.startsWith('http') ? source : `https://${source}`);
      let host = url.hostname.replace(/^www\./, '');
      // Map known domains to friendly names
      const nameMap = {
        'ba.n1info.com': 'N1 BiH',
        'hr.n1info.com': 'N1 Hrvatska',
        'rs.n1info.com': 'N1 Srbija',
        'n1info.ba': 'N1 BiH',
        'n1info.hr': 'N1 Hrvatska',
        'n1info.rs': 'N1 Srbija',
        'klix.ba': 'Klix',
        'istraga.ba': 'istraga',
        'index.hr': 'Index.hr',
        'vecernji.hr': 'Večernji list',
        'telegram.hr': 'Telegram',
        'slobodnadalmacija.hr': 'Slobodna Dalmacija',
        'srpskainfo.com': 'Srpskainfo',
        'blic.rs': 'Blic',
        'danas.rs': 'Danas',
        'kurir.rs': 'Kurir',
        'svet.rs': 'Svet',
        'alo.rs': 'Alo!',
        'cdm.me': 'CDM',
        'vijesti.me': 'Vijesti',
        'vijesti.ba': 'Vijesti.ba',
        'rtcg.me': 'RTCG',
        'slobodna-bosna.ba': 'Slobodna Bosna',
        'capital.ba': 'Capital',
        'nezavisne.com': 'Nezavisne Novine',
        'radiosarajevo.ba': 'Radio Sarajevo',
        'sportsport.ba': 'SportSport',
        'zdravija.ba': 'Zdravija',
        'avaz.ba': 'Avaz',
        'faktor.ba': 'Faktor',
        'oslobodjenje.ba': 'Oslobođenje',
        'mina.news': 'mina.',
      };
      // Check exact match first, then partial matches
      if (nameMap[host]) return nameMap[host];
      for (const [domain, name] of Object.entries(nameMap)) {
        if (host.includes(domain) || host.endsWith(domain)) return name;
      }
      // Fallback: capitalize domain parts
      return host.split('.').slice(0, -1).join('.') || host;
    } catch {
      return source;
    }
  }, []);

  // The server owns matching, including ANY/ALL keyword semantics.
  const filteredResults = results;

  const getSubcategoryPortalLabel = useCallback((tag) => {
    const portalNames = tagPortalsMap[tag];
    if (!portalNames || portalNames.length === 0) return '';
    const unique = Array.from(new Set(portalNames)).filter(Boolean);
    if (unique.length === 0) return '';
    return unique.slice(0, 3).join(', ');
  }, [tagPortalsMap]);

  // Update handleLogout to also clear admin status
  const handleLogout = useCallback(() => {
    trackEvent('logout', { target: 'account_logout' });
    clearAccountSession();
    capturedLoginFormDataRef.current = null;
    latestLoginFormDataRef.current = { identifier: '', password: '' };
    setLoginFormData(latestLoginFormDataRef.current);
    setLoginError(null);
    setLoginMessage(null);
    setShowLoginPassword(false);
    setSavedFilters([]);
    setActiveFilterName(null);
    setShowAccountDropdown(false);
    setShowFiltersDropdown(false);
    setShowMobileMenu(false);
    setHasUserClosedLoginDropdown(false);
    setShowLoginDropdown(true);
    setLoggedIn(false);
    setIsAdmin(false);
    setUsername('');
    setShowSubcategories(false);
    setMultiKeywordEnabled(false);
    setSearchAllEnabled(false);
    setSearchAll(false);
    setShowSubcategoryOnArticles(false);
    setShowPortalOnSubcategories(false);
  }, []);

  // Add useEffect to automatically open login dropdown for non-logged in users
  useEffect(() => {
    // Check if user is not logged in and we're not in initial loading phase
    if (!loggedIn && !hasUserClosedLoginDropdown) {
      setShowLoginDropdown(true);
    }
  }, [loggedIn, hasUserClosedLoginDropdown]);

  // Add useEffect to reset login dropdown state when user logs in
  // NOTE: Removed auto-close to prevent closing before user/me fetch completes
  // Dropdown will be closed manually in handleLoginSubmit after successful user/me fetch
  // useEffect(() => {
  //   if (loggedIn) {
  //     setShowLoginDropdown(false);
  //     setHasUserClosedLoginDropdown(false);
  //   }
  // }, [loggedIn]);

  // Add useEffect to handle ESC key for closing login dropdown
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showLoginDropdown) {
        captureLoginFormData();
        setShowLoginDropdown(false);
        setHasUserClosedLoginDropdown(true);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [showLoginDropdown, captureLoginFormData]);


  // Add useEffect to check for invalid tokens and open dropdown
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && !loggedIn && !hasUserClosedLoginDropdown) {
      // User has token but is not logged in (token might be invalid)
      console.log('User has token but not logged in, opening dropdown');
      setShowLoginDropdown(true);
    }
  }, [loggedIn, hasUserClosedLoginDropdown]);

  // Add useEffect to open dropdown when page is fully loaded
  useEffect(() => {
    const checkAndOpenDropdown = () => {
      const token = localStorage.getItem("token");
      const isLoggedIn = !!localStorage.getItem("token") && loggedIn;
      
      console.log('Page loaded check:', {
        hasToken: !!token,
        isLoggedIn,
        hasUserClosedLoginDropdown
      });
      
      if (!isLoggedIn && !hasUserClosedLoginDropdown) {
        console.log('Opening dropdown on page load');
        setShowLoginDropdown(true);
      }
    };
    
    // Check immediately to ensure everything is loaded
    checkAndOpenDropdown();
  }, [loggedIn, hasUserClosedLoginDropdown]);

  // NEW: Combined useEffect that fetches all initial data in one request
  useEffect(() => {
    const fetchAllInitialData = async () => {
      try {
        console.time('⏱️ COMBINED ENDPOINT /api/initial-data');
        const token = localStorage.getItem("token");
        const headers = {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/initial-data`, { headers });

        if (!response.ok) {
          throw new Error(`Failed to fetch initial data: ${response.status}`);
        }

        const data = await response.json();

        // Set maintenance mode
        if (data.maintenanceMode !== undefined) setMaintenanceMode(data.maintenanceMode);

        // Set all data at once
        if (data.categories) setCategories(data.categories);
        if (data.tags) setTags(data.tags);
        if (data.tagPortals) setTagPortalsMap(data.tagPortals);
        if (data.countries) setCountries(data.countries);
        if (data.portals) setPortals(data.portals);

        // Set commercial banner
        if (data.commercialBanners?.length) {
          setCommercialBanners(data.commercialBanners);
          const initialIndex = data.commercialBanners.findIndex(banner => banner.isInitial);
          setActiveBannerIndex(initialIndex >= 0 ? initialIndex : 0);
        }
        if (data.commercialBanner) {
          // Use the URL directly from backend (backend handles /api prefix)
          const bannerUrl = data.commercialBanner.url || '';
          setCommercialBanner(bannerUrl);

          if (data.commercialBanner && data.commercialBanner.link) {
            setCommercialBannerLink(data.commercialBanner.link);
          }

          if (data.commercialBanner.dimensions) {
            setCommercialDimensions({
              ...data.commercialBanner.dimensions,
              displayWidth: data.commercialBanner.dimensions.displayWidth || '100%'
            });
          }
        }

        // Mark basic data as loaded (categories, tags, countries, portals, banner)
        const basicDataLoaded = {
          categoriesLoaded: true,
          tagsLoaded: true,
          countriesLoaded: true,
          portalsLoaded: true,
          commercialBannerLoaded: true,
          firstArticlesLoaded: false,
          firstArticleImagesLoaded: false
        };

        const sharedFilter = readSharedFilter(window.location.search, data.categories || {});
        const sharedFilterState = sharedFilter
          ? buildFilterState(sharedFilter, data.categories || {})
          : null;
        const finishWithSharedFilter = () => {
          if (!sharedFilterState) return false;

          sharedFilterPresentRef.current = true;
          localStorage.removeItem(QUICK_TOPIC_KEY);
          isLoadingFilterFromServer.current = true;
          initialFilterForSearchRef.current = sharedFilterState;
          applyFilterState(sharedFilterState);

          setTimeout(() => {
            isLoadingFilterFromServer.current = false;
            setLoadingSequence({
              ...basicDataLoaded,
              favoriteFiltersLoaded: true,
              filterLoaded: true
            });
            countriesFetchedRef.current = true;
            portalsFetchedRef.current = true;
            favoriteFiltersFetchedRef.current = true;
          }, 0);

          return true;
        };

        if (!sharedFilterState) {
          sharedFilterPresentRef.current = false;
        }

        // Set user-specific data if authenticated
        if (data.authenticated && data.userInfo) {
          setLoggedIn(true);
          setIsAdmin(data.userInfo.admin);
          localStorage.setItem("isAdmin", data.userInfo.admin);
          setUsername(data.userInfo.username || '');
          setShowSubcategories(data.userInfo.show_subcategories || false);
          setShowSubcategoryOnArticles(data.userInfo.show_subcategory_on_articles ?? true);
          setMultiKeywordEnabled(data.userInfo.multi_keyword_enabled ?? false);
          setSearchAll(data.userInfo.search_all_enabled ?? false);
          setSearchAllEnabled(data.userInfo.search_all_enabled ?? false);
          setShowPortalOnSubcategories(data.userInfo.show_portal_on_subcategories ?? true);

          if (data.userInfo.theme !== undefined) {
            setDarkTheme(data.userInfo.theme);
          }

          if (sharedFilterState) {
            finishWithSharedFilter();
          } else if (hasCurrentFilter(data.userInfo.current_filter)) {
            const filter = data.userInfo.current_filter;
            console.log('Loading user filter from combined endpoint:', filter);
            storeCurrentFilter(filter);

            // Set flag to prevent filter change useEffect from triggering
            isLoadingFilterFromServer.current = true;

            const filterState = buildFilterState(filter, data.categories || {});
            initialFilterForSearchRef.current = filterState;
            applyFilterState(filterState);

            // Reset flag after filter is loaded (use setTimeout to ensure all setState calls complete)
            setTimeout(() => {
              isLoadingFilterFromServer.current = false;

              // Mark all as loaded AFTER filter state is updated to trigger initial search with correct values
              setLoadingSequence({
                ...basicDataLoaded,
                favoriteFiltersLoaded: true,
                filterLoaded: true
              });

              countriesFetchedRef.current = true;
              portalsFetchedRef.current = true;
              favoriteFiltersFetchedRef.current = true;
            }, 0);
          } else {
            storeCurrentFilter(null);
            initialFilterForSearchRef.current = null;
            // No filter to load, mark as loaded immediately
            setLoadingSequence({
              ...basicDataLoaded,
              favoriteFiltersLoaded: true,
              filterLoaded: true
            });
            portalsFetchedRef.current = true;
            countriesFetchedRef.current = true;
            favoriteFiltersFetchedRef.current = true;
          }

          if (data.favoriteFilters && Array.isArray(data.favoriteFilters)) {
            setSavedFilters(data.favoriteFilters);
          }
        } else if (!token) {
          storeCurrentFilter(null);
          setLoggedIn(false);
          setIsAdmin(false);
          localStorage.removeItem("isAdmin");

          if (!finishWithSharedFilter()) {
            initialFilterForSearchRef.current = null;
            // No user logged in, mark as loaded immediately
            setLoadingSequence({
              ...basicDataLoaded,
              favoriteFiltersLoaded: true,
              filterLoaded: true
            });
            portalsFetchedRef.current = true;
            countriesFetchedRef.current = true;
            favoriteFiltersFetchedRef.current = true;
          }
        } else {
          // Token exists but server says not authenticated (expired/invalid token)
          localStorage.removeItem("token");
          localStorage.removeItem("isAdmin");
          storeCurrentFilter(null);
          setLoggedIn(false);
          setIsAdmin(false);
          if (!finishWithSharedFilter()) {
            initialFilterForSearchRef.current = null;
            portalsFetchedRef.current = true;
            countriesFetchedRef.current = true;
            favoriteFiltersFetchedRef.current = true;
            setLoadingSequence({
              ...basicDataLoaded,
              favoriteFiltersLoaded: true,
              filterLoaded: true
            });
          }
        }
        console.timeEnd('⏱️ COMBINED ENDPOINT /api/initial-data');
        console.log("✅ Combined endpoint finished - all data loaded, triggering initial search");

      } catch (error) {
        console.error('Error fetching initial data:', error);

        // On error, still mark as loaded to prevent infinite loading
        setLoadingSequence({
          categoriesLoaded: true,
          tagsLoaded: true,
          countriesLoaded: true,
          portalsLoaded: true,
          favoriteFiltersLoaded: true,
          filterLoaded: true,
          commercialBannerLoaded: true,
          firstArticlesLoaded: false,
          firstArticleImagesLoaded: false
        });
      }
    };

    fetchAllInitialData();
  }, [applyFilterState, buildFilterState, handleLogout]);

  useEffect(() => {
    if (!showInterestPrompt) return undefined;
    const handleInterestPromptEscape = (event) => {
      if (event.key !== 'Escape') return;
      setShowInterestPrompt(false);
      trackEvent('interest_prompt_dismiss', {
        target: 'quick_topics',
        properties: { method: 'escape' }
      });
    };
    document.addEventListener('keydown', handleInterestPromptEscape);
    return () => document.removeEventListener('keydown', handleInterestPromptEscape);
  }, [showInterestPrompt, setShowInterestPrompt]);

  useEffect(() => {
    if (!showShareDialog) return undefined;
    const handleShareDialogEscape = (event) => {
      if (event.key === 'Escape') setShowShareDialog(false);
    };
    document.addEventListener('keydown', handleShareDialogEscape);
    return () => document.removeEventListener('keydown', handleShareDialogEscape);
  }, [showShareDialog]);

  // Preload banner image as soon as URL is available (before allDataLoaded)
  useEffect(() => {
    if (!commercialBanner) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = assetUrl(commercialBanner);
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
    };
  }, [commercialBanner]);

  // OLD: Update the useEffect that fetches favorite filters (DEPRECATED - now using combined endpoint)
  // Keeping for reference, but this is no longer called
  useEffect(() => {
    if (loadingSequence.favoriteFiltersLoaded) return; // Skip if already loaded by combined endpoint

    const fetchSavedFilters = async (retryCount = 0) => {
      const token = localStorage.getItem("token");
      // If no token, mark as loaded (user not logged in)
      if (!token) {
        setLoadingSequence(prev => {
          const newState = { ...prev, favoriteFiltersLoaded: true };
          return newState;
        });
        favoriteFiltersFetchedRef.current = true;
        return;
      }

      setIsLoadingFilters(true);
      try {
        console.time('⏱️ Fetch Favorite Filters');
        const response = await fetch(`${API_BASE_URL}/favorite-filters`, {
          headers: {
            "Authorization": `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (response.status === 401) {
          handleLogout();
          // Mark as loaded after logout
          setLoadingSequence(prev => {
            const newState = { ...prev, favoriteFiltersLoaded: true };
            return newState;
          });
          favoriteFiltersFetchedRef.current = true;
          setIsLoadingFilters(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch saved filters: ${response.status}`);
        }

        const data = await response.json();
        if (Array.isArray(data)) {
          setSavedFilters(data);
          console.timeEnd('⏱️ Fetch Favorite Filters');
          setLoadingSequence(prev => {
            const newState = { ...prev, favoriteFiltersLoaded: true };
            return newState;
          });
          favoriteFiltersFetchedRef.current = true;
          setIsLoadingFilters(false);
        } else {
          throw new Error('Invalid favorite filters data received');
        }
      } catch (error) {
        console.error('Error fetching favorite filters:', error);
        if (retryCount < 3 && !favoriteFiltersFetchedRef.current) {
          setTimeout(() => fetchSavedFilters(retryCount + 1), 1000 * (retryCount + 1));
        } else {
          // After max retries, mark as loaded anyway to prevent infinite loading
          setLoadingSequence(prev => {
            if (!prev.favoriteFiltersLoaded) {
              return { ...prev, favoriteFiltersLoaded: true };
            }
            return prev;
          });
          setIsLoadingFilters(false);
        }
      }
    };

    if (!favoriteFiltersFetchedRef.current) {
      fetchSavedFilters();
    }
  }, [handleLogout]);

  // Ažuriranje klase tijela pri promjeni teme
  useEffect(() => {
    if (darkTheme) {
      document.body.classList.add("dark-theme");
      document.body.classList.remove("light-theme");
      document.documentElement.classList.add("dark-theme");
      document.documentElement.classList.remove("light-theme");
    } else {
      document.body.classList.add("light-theme");
      document.body.classList.remove("dark-theme");
      document.documentElement.classList.add("light-theme");
      document.documentElement.classList.remove("dark-theme");
    }
    // Save theme preference to localStorage
    localStorage.setItem("theme", darkTheme);
  }, [darkTheme]);

  // OLD: Update the useEffect that fetches categories (DEPRECATED - now using combined endpoint)
  useEffect(() => {
    return; // DISABLED - using combined endpoint
    if (loadingSequence.categoriesLoaded) return; // Skip if already loaded by combined endpoint

    const fetchCategories = async () => {
      try {
        console.time('⏱️ Fetch Categories');
        const response = await fetch(`${API_BASE_URL}/categories`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch categories: ${response.status}`);
        }
        const data = await response.json();
        setCategories(data);
        console.timeEnd('⏱️ Fetch Categories');
        setLoadingSequence(prev => {
          const newState = { ...prev, categoriesLoaded: true };
          return newState;
        });
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Mark as loaded anyway to prevent infinite loading
        setLoadingSequence(prev => {
          if (!prev.categoriesLoaded) {
            return { ...prev, categoriesLoaded: true };
          }
          return prev;
        });
      }
    };

    // Only fetch if not already loaded by combined endpoint
    if (!loadingSequence.categoriesLoaded) {
      fetchCategories();
    }
  }, [loadingSequence.categoriesLoaded]);

  // OLD: Update the useEffect that fetches tags (DEPRECATED - now using combined endpoint)
  useEffect(() => {
    if (loadingSequence.tagsLoaded) return; // Skip if already loaded by combined endpoint

    const fetchTags = async () => {
      if (!loadingSequence.categoriesLoaded || loadingSequence.tagsLoaded) {
        return;
      }

      try {
        console.time('⏱️ Fetch Tags');
        const response = await fetch(`${API_BASE_URL}/tags`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch tags: ${response.status}`);
        }
        const data = await response.json();
        setTags(data);
        console.timeEnd('⏱️ Fetch Tags');
        setLoadingSequence(prev => {
          const newState = { ...prev, tagsLoaded: true };
          return newState;
        });
      } catch (error) {
        console.error('Error fetching tags:', error);
        // Mark as loaded anyway to prevent infinite loading
        setLoadingSequence(prev => {
          if (!prev.tagsLoaded) {
            return { ...prev, tagsLoaded: true };
          }
          return prev;
        });
      }
    };

    // Only fetch if not already loaded by combined endpoint
    if (!loadingSequence.tagsLoaded) {
      fetchTags();
    }
  }, [loadingSequence.categoriesLoaded, loadingSequence.tagsLoaded]);

  // OLD: Update the useEffect that fetches countries (DEPRECATED - now using combined endpoint)
  useEffect(() => {
    return; // DISABLED - using combined endpoint
    if (loadingSequence.countriesLoaded || countriesFetchedRef.current) return; // Skip if already loaded

    const fetchCountries = async (retryCount = 0) => {
      try {
        console.time('⏱️ Fetch Countries');
        const response = await fetch(`${API_BASE_URL}/countries`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch countries: ${response.status}`);
        }
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setCountries(data);
          console.timeEnd('⏱️ Fetch Countries');
          setLoadingSequence(prev => {
            const newState = { ...prev, countriesLoaded: true };
            return newState;
          });
          countriesFetchedRef.current = true;
        } else {
          throw new Error('Empty countries data received');
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        if (retryCount < 2 && !countriesFetchedRef.current) {
          setTimeout(() => fetchCountries(retryCount + 1), 1000 * (retryCount + 1));
        } else {
          setLoadingSequence(prev => {
            if (!prev.countriesLoaded) {
              return { ...prev, countriesLoaded: true };
            }
            return prev;
          });
        }
      }
    };

    if (!countriesFetchedRef.current && !loadingSequence.countriesLoaded) {
      fetchCountries();
      const timeout = setTimeout(() => {
        setLoadingSequence(prev => {
          if (!prev.countriesLoaded) {
            return { ...prev, countriesLoaded: true };
          }
          return prev;
        });
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [loadingSequence.countriesLoaded]);

  // OLD: Update the useEffect that fetches portals (DEPRECATED - now using combined endpoint)
  useEffect(() => {
    return; // DISABLED - using combined endpoint
    if (loadingSequence.portalsLoaded || portalsFetchedRef.current) return; // Skip if already loaded

    const fetchPortals = async (retryCount = 0) => {
      try {
        console.time('⏱️ Fetch Portals');
        const response = await fetch(`${API_BASE_URL}/portals`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch portals: ${response.status}`);
        }
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setPortals(data);
          console.timeEnd('⏱️ Fetch Portals');
          setLoadingSequence(prev => {
            const newState = { ...prev, portalsLoaded: true };
            return newState;
          });
          portalsFetchedRef.current = true;
        } else {
          throw new Error('Empty portals data received');
        }
      } catch (error) {
        console.error('Error fetching portals:', error);
        if (retryCount < 3 && !portalsFetchedRef.current) {
          setTimeout(() => fetchPortals(retryCount + 1), 1000 * (retryCount + 1));
        } else {
          setLoadingSequence(prev => {
            if (!prev.portalsLoaded) {
              return { ...prev, portalsLoaded: true };
            }
            return prev;
          });
        }
      }
    };

    if (!portalsFetchedRef.current && !loadingSequence.portalsLoaded) {
      fetchPortals();
    }
  }, [loadingSequence.portalsLoaded]);

  // OLD: Update the useEffect that fetches user info and current filter (DEPRECATED - now using combined endpoint)
  useEffect(() => {
    return; // DISABLED - using combined endpoint
    if (loadingSequence.filterLoaded) return; // Skip if already loaded by combined endpoint

    const fetchUserInfo = async () => {
      if (loadingSequence.filterLoaded) return;

      const token = localStorage.getItem("token");
      if (token) {
        try {
          console.time('⏱️ Fetch User Filter');
          const response = await fetch(`${API_BASE_URL}/user/me`, {
            headers: { 
              "Authorization": `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          if (!response.ok) {
            // Only throw error with status for 401/403 (unauthorized/forbidden)
            // For other errors (503, 500, etc.), don't remove token
            if (response.status === 401 || response.status === 403) {
              throw new Error(`Failed to fetch user info: ${response.status}`);
            } else {
              // For server errors, don't throw - just log and continue
              // Token might still be valid, just server issue
              console.warn("Server error fetching user info, but keeping token:", response.status);
              setLoadingSequence(prev => ({ ...prev, filterLoaded: true }));
              return;
            }
          }
          const data = await response.json();
          setLoggedIn(true);
          setIsAdmin(data.admin);
          localStorage.setItem("isAdmin", data.admin);
          setShowSubcategories(data.show_subcategories || false);
          setShowSubcategoryOnArticles(data.show_subcategory_on_articles ?? true);
          setMultiKeywordEnabled(data.multi_keyword_enabled ?? false);
          setSearchAll(data.search_all_enabled ?? false);
          setSearchAllEnabled(data.search_all_enabled ?? false);
          setShowPortalOnSubcategories(data.show_portal_on_subcategories ?? true);
          if (data.theme !== undefined) {
            setDarkTheme(data.theme);
          }
          if (data.current_filter) {
            setIncludeTags(data.current_filter.include_tags !== undefined ? data.current_filter.include_tags : true);
            setSelectedTags(migrateTagList(data.current_filter.selected_tags || [], categories));
            setFromDate(data.current_filter.from_date ? moment(data.current_filter.from_date, 'YYYY-MM-DDTHH:mm') : null);
            setToDate(data.current_filter.to_date ? moment(data.current_filter.to_date, 'YYYY-MM-DDTHH:mm') : null);
            setIncludePortals(data.current_filter.include_portals !== undefined ? data.current_filter.include_portals : true);
            setSelectedPortals(data.current_filter.selected_portals || []);
            setSelectedCountries(data.current_filter.selected_countries || []);
            restoreSearchOptions(data.current_filter);
            if (data.current_filter.query) {
              setQuery(data.current_filter.query);
              setAppliedQuery(data.current_filter.query);
            }
            if (data.current_filter.name) setActiveFilterName(data.current_filter.name);
          }
          console.timeEnd('⏱️ Fetch User Filter');
          setLoadingSequence(prev => ({ ...prev, filterLoaded: true }));
        } catch (error) {
          console.error("Error fetching user info:", error);
          // Only remove token if it's actually expired or invalid (401/403)
          // Don't remove token on network errors or other issues
          if (error.message && (error.message.includes('401') || error.message.includes('403'))) {
            setLoggedIn(false);
            setIsAdmin(false);
            localStorage.removeItem("isAdmin");
            localStorage.removeItem("token");
          } else {
            // For other errors (network, 503, etc.), keep token and just mark as not logged in
            // Token might still be valid, just couldn't verify due to server issues
            setLoggedIn(false);
            setIsAdmin(false);
          }
          setLoadingSequence(prev => ({ ...prev, filterLoaded: true }));
        }
      } else {
        setLoggedIn(false);
        setIsAdmin(false);
        localStorage.removeItem("isAdmin");
        setLoadingSequence(prev => ({ ...prev, filterLoaded: true }));
      }
    };

    fetchUserInfo();
  }, []);

  // Initial search - samo jednom kada se sve učita
  const initialSearchTriggered = useRef(false);
  const isLoadingFilterFromServer = useRef(false); // Prevent filter change trigger during initial load

  // Update the fetchResults function to handle errors better
  const fetchResults = async (newOffset = 0, isFreshSearch = false, queryOverride, filterOverride = null) => {
    // A fresh search always supersedes an older request. Pagination, however,
    // must wait for the current request to finish and respect hasMore.
    if (!isFreshSearch && (searchRequestInFlightRef.current || !hasMore)) {
      return;
    }

    let requestId = activeSearchRef.current.requestId;
    if (isFreshSearch) {
      activeSearchRef.current.controller?.abort();
      requestId += 1;
    }

    const controller = new AbortController();
    activeSearchRef.current = { requestId, controller };
    searchRequestInFlightRef.current = true;
    setIsLoading(true);
    setIsSearching(true);
    setError(null);

    try {
      const effectiveFilter = filterOverride || {};
      const effectiveSelectedTags = filterOverride ? (effectiveFilter.selectedTags || []) : selectedTags;
      const effectiveSelectedPortals = filterOverride ? (effectiveFilter.selectedPortals || []) : selectedPortals;
      const effectiveSelectedCountries = filterOverride ? (effectiveFilter.selectedCountries || []) : selectedCountries;
      const effectiveFromDate = filterOverride ? effectiveFilter.fromDate : fromDate;
      const effectiveToDate = filterOverride ? effectiveFilter.toDate : toDate;
      const effectiveIncludeTags = filterOverride ? effectiveFilter.includeTags : includeTags;
      const effectiveIncludePortals = filterOverride ? effectiveFilter.includePortals : includePortals;
      const requestQuery = queryOverride !== undefined
        ? queryOverride
        : (filterOverride ? effectiveFilter.query : appliedQuery);
      const searchTags = Array.from(new Set(
        effectiveSelectedTags
          .filter(t => t && typeof t === 'object' && t.type === 'subcategory' && t.name)
          .map(t => t.name)
      ));

      console.log('🔍 Tag filtering:', {
        selectedTags: effectiveSelectedTags,
        categoryNames: Object.keys(categories),
        searchTags
      });

      const cacheBuster = getArticleCacheBuster();
      const url = `${API_BASE_URL}/search`;
      const searchOptions = filterOverride ? {
        multi_keyword: multiKeywordEnabled && effectiveFilter.multiKeyword !== false,
        keyword_mode: effectiveFilter.keywordMode || 'any',
        search_all: searchAllEnabled && effectiveFilter.searchAll === true,
      } : isFreshSearch ? {
        multi_keyword: multiKeywordEnabled && multiKeyword,
        keyword_mode: keywordMode,
        search_all: searchAllEnabled && searchAll,
      } : appliedSearchOptions;
      if (isFreshSearch) setAppliedSearchOptions(searchOptions);
      const draftPayload = {
        ...searchOptions,
        q: requestQuery,
        tags: searchTags,
        portals: effectiveSelectedPortals,
        start_date: effectiveFromDate ? effectiveFromDate.format('YYYY-MM-DDTHH:mm') : '',
        end_date: effectiveToDate ? effectiveToDate.format('YYYY-MM-DDTHH:mm') : '',
        include_tags: effectiveIncludeTags,
        include_portals: effectiveIncludePortals,
        limit,
        offset: newOffset,
        ...(cacheBuster ? { cache_buster: cacheBuster } : {})
      };
      // Pagination belongs to the displayed feed, even while new filters are a draft.
      const searchPayload = !isFreshSearch && appliedSearchPayloadRef.current
        ? { ...appliedSearchPayloadRef.current, offset: newOffset }
        : draftPayload;

      console.log('Fetching articles with POST:', url, {
        ...searchPayload,
        tags: `${searchTags.length} selected`,
        portals: `${effectiveSelectedPortals.length} selected`
      });
      console.log('Filter state:', {
        query: requestQuery,
        liveQuery: query,
        selectedTags: effectiveSelectedTags,
        searchTags,
        selectedPortals: effectiveSelectedPortals,
        selectedCountries: effectiveSelectedCountries,
        fromDate: effectiveFromDate?.format('YYYY-MM-DDTHH:mm'),
        toDate: effectiveToDate?.format('YYYY-MM-DDTHH:mm'),
        includeTags: effectiveIncludeTags,
        includePortals: effectiveIncludePortals
      });

      // An empty result is valid: send this search once, without polling or retries.
      const response = await fetch(url, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}),
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(searchPayload),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch results: ${response.status}`);
      }

      const data = await response.json();

      if (requestId !== activeSearchRef.current.requestId) {
        return;
      }

      if (data.error) {
        throw new Error(data.error);
      }
      if (data.length < limit) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      // For fresh searches, replace results instead of merging
      if (isFreshSearch || newOffset === 0) {
        appliedSearchPayloadRef.current = searchPayload;
        setResults(data);
      } else {
        setResults(prev => {
          const uniqueArticles = [...prev];
          data.forEach(newArticle => {
            if (!uniqueArticles.some(existing => existing.link === newArticle.link)) {
              uniqueArticles.push(newArticle);
            }
          });
          return uniqueArticles;
        });
      }
      if (newOffset === 0 && isInitialLoad) {
        console.timeEnd('⏱️ Initial Search');
        console.timeEnd('🚀 TOTAL PAGE LOAD TIME');
        setLoadingSequence(prev => ({ ...prev, firstArticlesLoaded: true }));
        setIsInitialLoad(false);
      }
      setOffset(newOffset + limit);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching results:", error);
      if (requestId === activeSearchRef.current.requestId) {
        setError(error.message);
      }
    } finally {
      if (requestId === activeSearchRef.current.requestId &&
          controller === activeSearchRef.current.controller) {
        searchRequestInFlightRef.current = false;
        activeSearchRef.current.controller = null;
        setIsLoading(false);
        setIsSearching(false);
      }
    }
  };

  // Keep ref pointing at latest fetchResults implementation
  fetchResultsRef.current = fetchResults;

  // Recover banner metadata if the combined startup request failed. Also
  // refresh after editing banners and when connectivity returns.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/commercial-banners`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Banner unavailable');
        const banners = await response.json();
        if (cancelled) return;
        if (banners.length) {
          setCommercialBanners(banners);
          const initial = banners.findIndex(banner => banner.isInitial);
          setActiveBannerIndex(initial < 0 ? 0 : initial);
        } else {
          const legacyResponse = await fetch(`${API_BASE_URL}/commercial-banner`, { cache: 'no-store' });
          if (!legacyResponse.ok) return;
          const banner = await legacyResponse.json();
          if (cancelled) return;
          setCommercialBanner(banner.url || '');
          setCommercialBanners([]);
          setCommercialBannerLink(banner.link || '');
          if (banner.dimensions) setCommercialDimensions(banner.dimensions);
        }
      } catch {
        // Keep the current banner; an online/focus event can recover it later.
      }
    };
    const recover = () => { if (!commercialBanner) refresh(); };
    if (uiReady && !commercialBanner) refresh();
    window.addEventListener('commercialBannerUpdated', refresh);
    window.addEventListener('online', recover);
    window.addEventListener('focus', recover);
    return () => {
      cancelled = true;
      window.removeEventListener('commercialBannerUpdated', refresh);
      window.removeEventListener('online', recover);
      window.removeEventListener('focus', recover);
    };
  }, [uiReady, commercialBanner, API_BASE_URL]);

  const submitSearch = (origin = 'search_bar') => {
    // Synchronous guard also covers two clicks before React updates the buttons.
    if (searchRequestInFlightRef.current) return;
    const submittedQuery = keywordInputRef.current ? keywordInputRef.current.commit() : query;
    if (submittedQuery === null) return;
    setShowFilters(false);
    trackEvent('search_submit', {
      target: origin,
      properties: {
        query_length: submittedQuery.trim().length,
        has_query: submittedQuery.trim().length > 0,
        selected_tags_count: selectedTags.length,
        selected_portals_count: selectedPortals.length,
        selected_countries_count: selectedCountries.length,
        has_from_date: Boolean(fromDate),
        has_to_date: Boolean(toDate),
        include_tags: includeTags,
        include_portals: includePortals,
      },
    });
    setAppliedQuery(submittedQuery);
    setSearchHighlight(false);
    setIsSearching(true);
    setOffset(0);
    setHasMore(true);
    fetchResults(0, true, submittedQuery);
    if (origin === 'filters_panel') {
      requestAnimationFrame(() => {
        const heading = resultsHeadingRef.current;
        if (!heading) return;
        const stickyBottom = window.innerWidth <= 1024
          ? mobileStickyRef.current?.getBoundingClientRect().bottom || 0 : 0;
        heading.focus({ preventScroll: true });
        window.scrollTo({ top: Math.max(0, window.scrollY + heading.getBoundingClientRect().top - stickyBottom - 12), behavior: 'instant' });
      });
    }
  };

  const applyInterestCategory = (categoryName, origin = 'interest_prompt') => {
    const categoryTags = buildCategoryTags(categories, categoryName);
    if (categoryTags.length === 0) return;

    clearSharedFeedContext();
    clearQuickTopic();
    skipNextFilterHighlightRef.current = true;
    setSelectedPortals([]);
    setSelectedCountries([]);
    setFromDate(null);
    setToDate(null);
    setIncludeTags(true);
    setIncludePortals(true);
    restoreSearchOptions({});
    setQuery('');
    setAppliedQuery('');
    setActiveFilterName(null);
    storeCurrentFilter(null);
    setSelectedTags(categoryTags);
    setSearchHighlight(false);
    fetchResults(0, true, '', {
      selectedTags: categoryTags,
      selectedPortals: [],
      selectedCountries: [],
      fromDate: null,
      toDate: null,
      includeTags: true,
      includePortals: true,
      query: ''
    });
    trackEvent('quick_filter_apply', {
      target: categoryName,
      properties: {
        group: 'topic',
        origin,
        category: categoryName,
        selected_tags_count: categoryTags.length
      }
    });
  };

  const dismissInterestPrompt = (method = 'button') => {
    setShowInterestPrompt(false);
    trackEvent('interest_prompt_dismiss', {
      target: 'quick_topics',
      properties: { method }
    });
  };

  const handleInterestChoice = (categoryName) => {
    applyInterestCategory(categoryName, 'interest_prompt');
    setShowInterestPrompt(false);
    setShowFilters(false);
    trackEvent('interest_prompt_apply', { target: categoryName });
  };

  // Konsolidovani useEffect za inicijalni search - samo jednom kada se sve učita
  useEffect(() => {
    // Provjeri da li su svi podaci učitani
    const allDataLoaded = loadingSequence.categoriesLoaded &&
                          loadingSequence.tagsLoaded &&
                          loadingSequence.countriesLoaded &&
                          loadingSequence.portalsLoaded &&
                          loadingSequence.favoriteFiltersLoaded &&
                          loadingSequence.filterLoaded;

    // Ako su svi podaci učitani i još nismo pokrenuli inicijalni search
    if (allDataLoaded && !initialSearchTriggered.current) {
      console.time('⏱️ Initial Search');
      initialSearchTriggered.current = true;
      // Pokreni inicijalni search preko ref-a (uvijek pokazuje na zadnji
      // closure sa svježim filter state-om), uz mali setTimeout da se
      // state set-ovi iz fetchAllInitialData garantovano commit-uju.
      setTimeout(() => {
        const fn = fetchResultsRef.current || fetchResults;
        const initialFilter = initialFilterForSearchRef.current;
        fn(0, true, initialFilter ? initialFilter.query : undefined, initialFilter);
        initialFilterForSearchRef.current = null;
      }, 0);
    }
  }, [loadingSequence.categoriesLoaded, loadingSequence.tagsLoaded, loadingSequence.countriesLoaded, loadingSequence.portalsLoaded, loadingSequence.favoriteFiltersLoaded, loadingSequence.filterLoaded]);

  // Izmjene filtera ostaju neprimijenjene dok korisnik ne pritisne Pretraži.
  // Ovo sprečava paralelne zahtjeve sa djelimično odabranim filterima.
  useEffect(() => {
    // Ne pozivaj search dok se ne učita sve potrebno
    if (!initialSearchTriggered.current) {
      return;
    }

    // Ne pozivaj search dok se učitava filter sa servera
    if (isLoadingFilterFromServer.current) {
      console.log('Skipping filter change search - loading filter from server');
      return;
    }

    // Ne pozivaj search na inicijalnom učitavanju
    if (isInitialLoad) {
      return;
    }

    if (skipNextFilterHighlightRef.current) {
      skipNextFilterHighlightRef.current = false;
      return;
    }

    setSearchHighlight(true);
    // isInitialLoad is intentionally omitted: finishing the initial request is
    // not a user filter change and must not mark the search as pending.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTags, selectedPortals, selectedCountries, fromDate, toDate, includeTags, includePortals, multiKeyword, keywordMode, searchAll]);

  // Typing remains a draft until the next explicit search submission.

  // DEPRECATED: filter is now loaded by the combined /api/initial-data endpoint
  // (see fetchAllInitialData above). Running this in parallel caused a race
  // where the reset (setSelectedTags([]), setSelectedPortals([])...) could
  // overwrite filter state already applied by the combined endpoint, leaving
  // the initial search running with default (unfiltered) state — user had to
  // press "Pretraži" to actually apply their last filter.
  useEffect(() => {
    return; // DISABLED - using combined endpoint
    // eslint-disable-next-line no-unreachable
    const loadCurrentFilter = async () => {
      if (loadingSequence.filterLoaded) return;

      const token = localStorage.getItem("token");
      if (!token) {
        setLoadingSequence(prev => ({ ...prev, filterLoaded: true }));
        return;
      }

      try {
        // Clear any existing filter from localStorage during initial load
        if (isInitialLoad) {
          localStorage.removeItem("currentFilter");
        }

        // First try to get current filter from the server
        const response = await fetch(`${API_BASE_URL}/current-filter`, {
          headers: { 
            "Authorization": `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.ok) {
          const filter = await response.json();
          if (filter) {
            localStorage.setItem("currentFilter", JSON.stringify(filter));

            // Reset all filter states before applying new ones
            isLoadingFilterFromServer.current = true;
            setIncludeTags(true);
            setSelectedTags([]);
            setFromDate(null);
            setToDate(null);
            setIncludePortals(true);
            setSelectedPortals([]);
            restoreSearchOptions({});
            setQuery('');
            setAppliedQuery('');

            // Update all filter states in a batch
            const updates = () => {
              setIncludeTags(filter.include_tags !== undefined ? filter.include_tags : true);
              setSelectedTags(migrateTagList(filter.selected_tags, categories));
              setFromDate(filter.from_date ? moment(filter.from_date, 'YYYY-MM-DDTHH:mm') : null);
              setToDate(filter.to_date ? moment(filter.to_date, 'YYYY-MM-DDTHH:mm') : null);
              setIncludePortals(filter.include_portals !== undefined ? filter.include_portals : true);
              setSelectedPortals(filter.selected_portals);
              restoreSearchOptions(filter);
              if (filter.query) {
                setQuery(filter.query);
                setAppliedQuery(filter.query);
              }
              setLoadingSequence(prev => ({ ...prev, filterLoaded: true }));
              if (filter.name) setActiveFilterName(filter.name);
              setTimeout(() => { isLoadingFilterFromServer.current = false; }, 0);
            };

            // Execute updates
            updates();
            return;
          }
        }

        // If server request fails, try localStorage
        const savedFilter = localStorage.getItem("currentFilter");
        if (savedFilter) {
          const filter = JSON.parse(savedFilter);

          // Reset all filter states before applying new ones
          setIncludeTags(true);
          setSelectedTags([]);
          setFromDate(null);
          setToDate(null);
          setIncludePortals(true);
          setSelectedPortals([]);
          restoreSearchOptions({});
          setQuery('');
          setAppliedQuery('');

          // Update all filter states in a batch
          isLoadingFilterFromServer.current = true;
          const updates = () => {
            setIncludeTags(filter.include_tags !== undefined ? filter.include_tags : true);
            setSelectedTags(migrateTagList(filter.selected_tags, categories));
            setFromDate(filter.from_date ? moment(filter.from_date, 'YYYY-MM-DDTHH:mm') : null);
            setToDate(filter.to_date ? moment(filter.to_date, 'YYYY-MM-DDTHH:mm') : null);
            setIncludePortals(filter.include_portals !== undefined ? filter.include_portals : true);
            setSelectedPortals(filter.selected_portals);
            setSelectedCountries(filter.selected_countries || []);
            restoreSearchOptions(filter);
            if (filter.query) {
              setQuery(filter.query);
              setAppliedQuery(filter.query);
            }
            setLoadingSequence(prev => ({ ...prev, filterLoaded: true }));
            setTimeout(() => { isLoadingFilterFromServer.current = false; }, 0);
          };

          // Execute updates
          updates();
        } else {
          setLoadingSequence(prev => ({ ...prev, filterLoaded: true }));
        }
      } catch (error) {
        console.error("Error loading current filter:", error);
        setLoadingSequence(prev => ({ ...prev, filterLoaded: true }));
      }
    };

    loadCurrentFilter();
  }, []);


  // Use refs to store latest values for observer callback
  const offsetRef = useRef(offset);
  const hasMoreRef = useRef(hasMore);
  const isLoadingRef = useRef(isLoading);
  const queryRef = useRef(query);
  const appliedQueryRef = useRef(appliedQuery);
  
  // Update refs when values change
  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  useEffect(() => {
    queryRef.current = query;
  }, [query]);
  useEffect(() => {
    appliedQueryRef.current = appliedQuery;
  }, [appliedQuery]);

  // Infinite scroll: učitaj više kad je zadnji članak u prikazu
  // Create observer once and reuse it
  useEffect(() => {
    const observerCallback = (entries) => {
      // Use refs to get latest values
      const currentOffset = offsetRef.current;
      const currentHasMore = hasMoreRef.current;
      const currentIsLoading = isLoadingRef.current;
      const liveQueryChanged = normalizeSearchText(queryRef.current).trim() !==
        normalizeSearchText(appliedQueryRef.current).trim();
      if (liveQueryChanged) return;
      if (entries[0].isIntersecting && currentHasMore && !currentIsLoading) {
        if (fetchResultsRef.current) {
          fetchResultsRef.current(currentOffset);
        }
      }
    };
    observer.current = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '200px',
      threshold: 0.1,
    });
    if (lastArticleRef.current) {
      observer.current.observe(lastArticleRef.current);
    }
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []); // Create observer only once

  // Scroll-based fallback for mobile browsers where IntersectionObserver
  // can miss events due to compositing/repaint timing
  useEffect(() => {
    const handleScroll = () => {
      if (!hasMoreRef.current || isLoadingRef.current) return;
      const liveQueryChanged = normalizeSearchText(queryRef.current).trim() !==
        normalizeSearchText(appliedQueryRef.current).trim();
      if (liveQueryChanged) return;
      const scrollY = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (docHeight - scrollY - windowHeight < 400) {
        if (fetchResultsRef.current) {
          fetchResultsRef.current(offsetRef.current);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Release time-input focus after an outside click without moving controls on press.
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside time input fields
      if (timeInputFocused && 
          !event.target.closest('.time-picker') && 
          !event.target.closest('.datetime-input-container')) {
        setTimeInputFocused(false);
        // useEffect će automatski reagovati na promjenu timeInputFocused
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [timeInputFocused]);

  // Prebacivanje vidljivosti filtera
  // Mobile/tablet: open panel under sticky bar; return to it if scrolled out of view.
  const toggleFilters = () => {
    const isMobile = window.innerWidth <= 1024;
    trackEvent('filters_toggle', {
      target: 'filters_panel',
      properties: {
        action: showFilters ? 'close' : 'open',
        is_mobile: isMobile,
      },
    });

    const scrollFiltersUnderStickyBar = () => {
      const filtersEl = filtersRef.current;
      const stickyEl = mobileStickyRef.current;
      if (!filtersEl || !stickyEl) return;
      const stickyRect = stickyEl.getBoundingClientRect();
      const filtersRect = filtersEl.getBoundingClientRect();
      const targetY = window.scrollY + filtersRect.top - stickyRect.bottom;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    };

    if (!isMobile) {
      setShowFilters(prev => !prev);
      return;
    }

    if (!showFilters) {
      setShowFilters(true);
      // Wait for the panel to mount before measuring/scrolling
      requestAnimationFrame(() => requestAnimationFrame(scrollFiltersUnderStickyBar));
      return;
    }

    // Filters are open: close if visible, otherwise scroll back to them
    const filtersEl = filtersRef.current;
    const stickyEl = mobileStickyRef.current;
    if (!filtersEl || !stickyEl) {
      setShowFilters(false);
      return;
    }
    const stickyRect = stickyEl.getBoundingClientRect();
    const filtersRect = filtersEl.getBoundingClientRect();
    const isVisible = filtersRect.bottom > stickyRect.bottom && filtersRect.top < window.innerHeight;
    if (isVisible) {
      setShowFilters(false);
    } else {
      scrollFiltersUnderStickyBar();
    }
  };

  // Add new handler for group expansion - only one open at a time
  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      [groupName]: !prev[groupName]
    }));
  };

  // Modify handleTagChange to work with categories
  const handleTagChange = (tag, isMainCategory = false, mainCategory = null) => {
    clearQuickTopic();
    const wasSelected = isMainCategory
      ? hasCategoryTag(selectedTags, tag)
      : hasSubcategoryTag(selectedTags, tag, mainCategory);

    trackEvent('filter_tag_toggle', {
      target: tag,
      properties: {
        action: wasSelected ? 'remove' : 'add',
        type: isMainCategory ? 'category' : 'subcategory',
        parent: mainCategory,
      },
    });

    if (isMainCategory) {
      const subcategories = categories[tag] || [];
      setSelectedTags(prev => {
        if (hasCategoryTag(prev, tag)) {
          return prev.filter(t =>
            !tagIsCategory(t, tag) &&
            !(t && typeof t === 'object' && t.type === 'subcategory' && t.parent === tag && subcategories.includes(t.name))
          );
        }
        const additions = [
          { type: 'category', name: tag },
          ...subcategories.map(sub => ({ type: 'subcategory', name: sub, parent: tag }))
        ];
        return dedupeTags([...prev, ...additions]);
      });
    } else {
      setSelectedTags(prev => {
        const exists = hasSubcategoryTag(prev, tag, mainCategory);
        let newTags = exists
          ? prev.filter(t => !tagIsSubcategory(t, tag, mainCategory))
          : [...prev, { type: 'subcategory', name: tag, parent: mainCategory }];

        Object.entries(categories).forEach(([category, subcategories]) => {
          const allSubcategoriesSelected = subcategories.length > 0 && subcategories.every(sub =>
            newTags.some(t => tagIsSubcategory(t, sub, category))
          );
          const categoryIndex = newTags.findIndex(t => tagIsCategory(t, category));
          if (allSubcategoriesSelected && categoryIndex === -1) {
            newTags.push({ type: 'category', name: category });
          } else if (!allSubcategoriesSelected && categoryIndex !== -1) {
            newTags = newTags.filter((_, i) => i !== categoryIndex);
          }
        });

        return newTags;
      });
    }
  };

  // Replace renderTagGroups with new version using categories
  const renderTagGroups = () => {
    const visibleCategoryEntries = Object.entries(categories).filter(([, subcategories]) =>
      Array.isArray(subcategories) && subcategories.length > 0
    );

    return (
    <div className={`tag-list ${!includeTags ? 'red-checkbox' : ''}`}>
      {visibleCategoryEntries.map(([categoryName, subcategories]) => (
        <React.Fragment key={categoryName}>
        <div
          className="tag-group"
        >
          <div className="group-header" onClick={() => handleTagChange(categoryName, true)}>
            <label className="tag-checkbox" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                value={categoryName}
                onChange={() => handleTagChange(categoryName, true)}
                checked={hasCategoryTag(selectedTags, categoryName)}
              />
              {categoryName}
            </label>
            {showSubcategories && (
              <button
                type="button"
                className={`expand-button${expandedGroups[categoryName] ? ' open' : ''}`}
                aria-expanded={Boolean(expandedGroups[categoryName])}
                aria-label={`${expandedGroups[categoryName] ? 'Zatvori' : 'Otvori'} subkategorije za ${categoryName}`}
                onClick={(e) => { e.stopPropagation(); toggleGroup(categoryName); }}
              >
                {expandedGroups[categoryName] ? '▲' : '▼'}
              </button>
            )}
          </div>
        </div>
          {showSubcategories && expandedGroups[categoryName] && (
            <div className="group-tags category-subcategories" role="group" aria-label={`Potkategorije: ${categoryName}`}>
              <div className="subcategory-heading">{categoryName}</div>
              {subcategories.map((tag) => {
                const portalLabel = showPortalOnSubcategories ? getSubcategoryPortalLabel(tag) : '';
                return (
                <div key={tag} className="subtag-row" onClick={() => handleTagChange(tag, false, categoryName)}>
                  <label className="tag-checkbox subtag" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      value={tag}
                      onChange={() => handleTagChange(tag, false, categoryName)}
                      checked={hasSubcategoryTag(selectedTags, tag, categoryName)}
                    />
                    {tag}
                    {portalLabel && (
                      <span className="subtag-portal"> ({portalLabel})</span>
                    )}
                  </label>
                </div>
                );
              })}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
  };

  // Handleri za odabir oznaka, portala i država
  const handlePortalChange = (portal) => {
    trackEvent('filter_portal_toggle', {
      target: portal,
      properties: {
        action: selectedPortals.includes(portal) ? 'remove' : 'add',
      },
    });

    setSelectedPortals(prev => {
      const newSelected = prev.includes(portal) ? prev.filter(p => p !== portal) : [...prev, portal];

      // Auto-select/deselect country based on whether all its portals are selected
      const portalObj = portals.find(p => p.name === portal);
      if (portalObj && portalObj.country) {
        const country = portalObj.country;
        const portalsInCountry = portals.filter(p => p.country === country).map(p => p.name);
        const allSelected = portalsInCountry.every(p => newSelected.includes(p));
        setSelectedCountries(prevCountries =>
          allSelected
            ? [...new Set([...prevCountries, country])]
            : prevCountries.filter(c => c !== country)
        );
      }

      return newSelected;
    });
  };

  const handleCountryChange = (country) => {
    trackEvent('filter_country_toggle', {
      target: country,
      properties: {
        action: selectedCountries.includes(country) ? 'remove' : 'add',
      },
    });

    setSelectedCountries(prev => {
      const isCurrentlySelected = prev.includes(country);
      const newCountries = isCurrentlySelected 
        ? prev.filter(c => c !== country) 
        : [...prev, country];
      
      // Automatically select/deselect all portals from the selected countries
      const portalsFromSelectedCountries = portals
        .filter(portal => newCountries.includes(portal.country))
        .map(portal => portal.name);
      
      setSelectedPortals(prevPortals => {
        let nextPortals;
        if (!isCurrentlySelected) {
          // Country was selected - add all portals from this country
          nextPortals = [...new Set([...prevPortals, ...portalsFromSelectedCountries])];
        } else {
          // Country was deselected - remove all portals from this country
          const portalsFromDeselectedCountry = portals
            .filter(portal => portal.country === country)
            .map(portal => portal.name);
          nextPortals = prevPortals.filter(portal => !portalsFromDeselectedCountry.includes(portal));
        }
        return nextPortals;
      });
      
      return newCountries;
    });
  };

  const handleClearTags = () => {
    clearQuickTopic();
    trackEvent('filters_clear', {
      target: 'tags',
      properties: { selected_tags_count: selectedTags.length },
    });
    setSelectedTags([]);
  };
  const handleClearPortals = () => {
    trackEvent('filters_clear', {
      target: 'portals',
      properties: {
        selected_portals_count: selectedPortals.length,
        selected_countries_count: selectedCountries.length,
      },
    });
    setSelectedPortals([]);
    setSelectedCountries([]);
  };

  const handleRequestClearAllFilters = () => {
    if (!hasActiveFilters) return;
    setShowClearAllConfirmation(true);
  };

  const handleConfirmClearAllFilters = async () => {
    trackEvent('filters_clear', {
      target: 'all',
      properties: {
        selected_tags_count: selectedTags.length,
        selected_portals_count: selectedPortals.length,
        selected_countries_count: selectedCountries.length,
        had_query: query.trim().length > 0,
        had_time_filter: Boolean(fromDate || toDate),
      },
    });
    setShowClearAllConfirmation(false);
    clearSharedFeedContext();
    clearQuickTopic();
    setSelectedTags([]);
    setSelectedPortals([]);
    setSelectedCountries([]);
    setFromDate(null);
    setToDate(null);
    setIncludeTags(true);
    setIncludePortals(true);
    restoreSearchOptions({});
    setQuery('');
    setAppliedQuery('');
    setActiveFilterName(null);
    setSearchHighlight(false);
    storeCurrentFilter(null);

    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/current-filter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error("Error clearing current filter:", error);
    }
  };

  const getAutomaticShareName = () => {
    if (activeFilterName) return activeFilterName;

    const selectedCategoryNames = selectedTags
      .filter(tag => tag?.type === 'category' && tag.name)
      .map(tag => tag.name);
    if (selectedCategoryNames.length === 1) {
      return `${selectedCategoryNames[0]} feed`;
    }

    if (query.trim()) return `Pretraga: ${query.trim().slice(0, 50)}`;
    if (selectedPortals.length === 1) return `${selectedPortals[0]} feed`;
    if (selectedCountries.length === 1) return `${selectedCountries[0]} feed`;

    return 'Moj JaoSVE feed';
  };

  const openShareDialog = () => {
    if (!hasActiveFilters) return;
    setShareFeedback('');
    setShowShareDialog(true);
    trackEvent('filter_share_open', {
      target: 'current_filter',
      properties: {
        selected_tags_count: selectedTags.length,
        selected_portals_count: selectedPortals.length,
        selected_countries_count: selectedCountries.length,
      },
    });
  };

  const createCurrentShareUrl = () => createShareableFilterUrl({
    query,
    multi_keyword: multiKeywordEnabled && multiKeyword,
    keyword_mode: keywordMode,
    search_all: searchAllEnabled && searchAll,
    include_tags: includeTags,
    selected_tags: selectedTags,
    from_date: fromDate ? fromDate.format('YYYY-MM-DDTHH:mm') : '',
    to_date: toDate ? toDate.format('YYYY-MM-DDTHH:mm') : '',
    include_portals: includePortals,
    selected_portals: selectedPortals,
    selected_countries: selectedCountries,
  }, process.env.REACT_APP_SITE_URL ? `${process.env.REACT_APP_SITE_URL}/` : window.location.href);

  const copyShareLink = async () => {
    const shareUrl = createCurrentShareUrl();

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!copied) throw new Error('Copy command failed');
      }

      setShareFeedback('Link je kopiran.');
      trackEvent('filter_share_copy', { target: 'current_filter' });
    } catch (error) {
      console.error('Filter link copy failed:', error);
      setShareFeedback('Kopiranje nije uspjelo.');
    }
  };

  const shareFilterNatively = async () => {
    if (!navigator.share) return;

    const name = getAutomaticShareName();
    const shareData = {
      title: name,
      text: `${name} na JaoSVE`,
      url: createCurrentShareUrl(),
    };

    try {
      await navigator.share(shareData);
      setShowShareDialog(false);
      trackEvent('filter_share_native', { target: 'current_filter' });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Native filter share failed:', error);
        setShareFeedback('Dijeljenje nije uspjelo.');
      }
    }
  };

  // --- Funkcije za spremljene filtere (putem /api/favorite-filters) ---

  // Otvori input za ime novog filtera (bez spremanja u bazu)
  const handleSaveCurrentFilter = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Morate biti prijavljeni da biste spremili filtere.");
      return;
    }
    trackEvent('saved_filter_start', {
      target: 'create_filter',
      properties: {
        selected_tags_count: selectedTags.length,
        selected_portals_count: selectedPortals.length,
        selected_countries_count: selectedCountries.length,
      },
    });
    setShowFiltersDropdown(true);
    setIsCreatingNewFilter(true);
    // Generiši unikatno ime filtera
    let filterNum = savedFilters.length + 1;
    const existingNames = savedFilters.map(f => f.name);
    while (existingNames.includes(`filter${filterNum}`)) {
      filterNum++;
    }
    setNewFilterName(`filter${filterNum}`);
    // On mobile, open the hamburger menu so the filter dropdown is visible
    if (window.innerWidth <= 768) {
      setShowMobileMenu(true);
    }
  };

  // Potvrdi i spremi novi filter u bazu
  const confirmSaveNewFilter = async () => {
    if (!newFilterName || newFilterName.trim() === "") {
      alert("Ime filtera ne može biti prazno.");
      return;
    }
    if (savedFilters.some(f => f.name === newFilterName.trim())) {
      alert("Filter sa tim imenom već postoji. Odaberite drugo ime.");
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Morate biti prijavljeni da biste spremili filtere.");
      return;
    }
    const newFilter = {
      name: newFilterName.trim(),
      query,
      multi_keyword: multiKeywordEnabled && multiKeyword,
      keyword_mode: keywordMode,
      search_all: searchAllEnabled && searchAll,
      include_tags: includeTags,
      selected_tags: selectedTags,
      from_date: fromDate ? fromDate.format('YYYY-MM-DDTHH:mm') : "",
      to_date: toDate ? toDate.format('YYYY-MM-DDTHH:mm') : "",
      include_portals: includePortals,
      selected_portals: selectedPortals,
      selected_countries: selectedCountries,
    };
    try {
      const response = await fetch(`${API_BASE_URL}/favorite-filters`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(newFilter),
      });
      if (response.status === 401) {
        handleLogout();
        return;
      }
      if (response.ok) {
        const saved = await response.json();
        trackEvent('saved_filter_created', {
          target: 'saved_filter',
          properties: {
            selected_tags_count: selectedTags.length,
            selected_portals_count: selectedPortals.length,
            selected_countries_count: selectedCountries.length,
            has_time_filter: Boolean(fromDate || toDate),
          },
        });
        setSavedFilters(prev => [...prev, saved]);
        setActiveFilterName(saved.name);
        setIsCreatingNewFilter(false);
        setNewFilterName('');

        // Also save as current filter
        await fetch(`${API_BASE_URL}/current-filter`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(newFilter),
        });
        localStorage.setItem("currentFilter", JSON.stringify(newFilter));
      } else {
        const err = await response.json();
        alert(err.error || "Spremanje filtera nije uspjelo");
      }
    } catch (error) {
      console.error("Greška prilikom spremanja filtera:", error);
      alert("Došlo je do greške prilikom spremanja filtera.");
    }
  };

  // Odustani od kreiranja novog filtera
  const cancelNewFilter = () => {
    setIsCreatingNewFilter(false);
    setNewFilterName('');
  };

  // Ažuriraj filter sa window.confirm dijalogom
  const handleUpdateCurrentFilter = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Morate biti prijavljeni da biste ažurirali filtere.");
      return;
    }
    if (!savedFilters || savedFilters.length === 0) {
      alert("Prvo morate kreirati filter da bi ga mogli ažurirati.");
      return;
    }
    const filterLabel = activeFilterName ? `"${activeFilterName}"` : "ovaj filter";
    if (!window.confirm(`Jeste li sigurni da želite ažurirati filter ${filterLabel}?`)) {
      return;
    }
    confirmUpdateCurrentFilter();
  };

  // Potvrdi i izvrši ažuriranje filtera
  const confirmUpdateCurrentFilter = async () => {
    setShowUpdateConfirmation(false);
    setShowFiltersDropdown(false);
    setShowMobileMenu(false);
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Morate biti prijavljeni da biste ažurirali filtere.");
      return;
    }

    // Get the current filter name from the database
    let currentFilterName = null;
    try {
      const currentFilterResponse = await fetch(`${API_BASE_URL}/current-filter`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (currentFilterResponse.ok) {
        const currentFilterData = await currentFilterResponse.json();
        currentFilterName = currentFilterData.name;
      }
    } catch (error) {
      console.error("Error fetching current filter:", error);
    }

    const currentFilter = {
      id: savedFilters.find(filter => filter.name === currentFilterName)?.id,
      query,
      multi_keyword: multiKeywordEnabled && multiKeyword,
      keyword_mode: keywordMode,
      search_all: searchAllEnabled && searchAll,
      include_tags: includeTags,
      selected_tags: selectedTags,
      from_date: fromDate ? fromDate.format('YYYY-MM-DDTHH:mm') : "",
      to_date: toDate ? toDate.format('YYYY-MM-DDTHH:mm') : "",
      include_portals: includePortals,
      selected_portals: selectedPortals,
      selected_countries: selectedCountries,
      ...(currentFilterName && { name: currentFilterName })
    };

    try {
      // Update current filter on server
      const response = await fetch(`${API_BASE_URL}/current-filter`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(currentFilter),
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (response.ok) {
        trackEvent('saved_filter_updated', {
          target: 'saved_filter',
          properties: {
            selected_tags_count: selectedTags.length,
            selected_portals_count: selectedPortals.length,
            selected_countries_count: selectedCountries.length,
            has_time_filter: Boolean(fromDate || toDate),
          },
        });
        // Save to localStorage as well
        localStorage.setItem("currentFilter", JSON.stringify(currentFilter));

        // Update savedFilters state to reflect the change
        setSavedFilters(prev =>
          prev.map(filter => {
            // Check if this filter matches the current one by name
            if (filter.name === currentFilter.name) {
              return currentFilter;
            }
            return filter;
          })
        );

        await response.json();
      } else {
        const err = await response.json();
        if (err.status === 'error' && err.message === 'Database connection error. Please try again.') {
          alert("Greška s bazom podataka. Molimo pokušajte ponovo za nekoliko sekundi.");
        } else if (err.status === 'error' && err.message === 'Failed to update current filter') {
          alert("Greška s bazom podataka. Molimo pokušajte ponovo za nekoliko sekundi.");
        } else {
          alert(err.error || err.message || "Ažuriranje filtera nije uspjelo");
        }
      }
    } catch (error) {
      console.error("Greška prilikom ažuriranja filtera:", error);
      alert("Problema s mrežom ili serverom. Molimo pokušajte ponovo.");
    }
  };

  // Primijeni spremljeni filter ažuriranjem stanja i osvježi rezultate
  const applySavedFilter = async (filter) => {
    const appliedFilter = buildFilterState(filter, categories);
    trackEvent('saved_filter_applied', {
      target: 'saved_filter',
      properties: {
        selected_tags_count: Array.isArray(filter.selected_tags) ? filter.selected_tags.length : 0,
        selected_portals_count: Array.isArray(filter.selected_portals) ? filter.selected_portals.length : 0,
        selected_countries_count: Array.isArray(filter.selected_countries) ? filter.selected_countries.length : 0,
        has_time_filter: Boolean(filter.from_date || filter.to_date),
      },
    });
    clearSharedFeedContext();
    skipNextFilterHighlightRef.current = true;
    setIncludeTags(appliedFilter.includeTags);
    setSelectedTags(appliedFilter.selectedTags);
    setFromDate(appliedFilter.fromDate);
    setToDate(appliedFilter.toDate);
    setIncludePortals(appliedFilter.includePortals);
    setSelectedPortals(appliedFilter.selectedPortals);
    setSelectedCountries(appliedFilter.selectedCountries);
    setMultiKeyword(appliedFilter.multiKeyword);
    setKeywordMode(appliedFilter.keywordMode);
    setSearchAll(appliedFilter.searchAll);
    setQuery(appliedFilter.query);
    setAppliedQuery(appliedFilter.query);
    setSearchHighlight(false);
    if (filter.name) setActiveFilterName(filter.name);

    fetchResults(0, true, appliedFilter.query, appliedFilter);
    
    // Save as current filter
    const token = localStorage.getItem("token");
    if (token) {
      try {
        // Save to server
        await fetch(`${API_BASE_URL}/current-filter`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(filter),
        });
        
        // Save to localStorage
        console.log("Saving current filter to localStorage:", filter);
        localStorage.setItem("currentFilter", JSON.stringify(filter));
      } catch (error) {
        console.error("Error saving current filter:", error);
      }
    }
    
    // Vrati na vrh stranice
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Zatvori dropdown i hamburger meni
    setShowFiltersDropdown(false);
    setShowMobileMenu(false);
  };

  // Briši spremljeni filter putem API-ja koristeći njegov indeks
  const handleDeleteFilter = async (index) => {
    if (window.confirm("Jeste li sigurni da želite obrisati ovaj filter?")) {
      const token = localStorage.getItem("token");
      try {
        const response = await fetch(`${API_BASE_URL}/favorite-filters/${index}`, {
          method: "DELETE",
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (response.status === 401) {
          handleLogout();
          return;
        }
        if (response.ok) {
          const result = await response.json();
          trackEvent('saved_filter_deleted', {
            target: 'saved_filter',
          });
          setSavedFilters(prev => {
            const newFilters = [...prev];
            newFilters.splice(index, 1);
            return newFilters;
          });
          // If the deleted filter was the active/current filter, clear it
          if (result.current_filter_cleared) {
            setActiveFilterName(null);
            localStorage.removeItem("currentFilter");
          }
        } else {
          const err = await response.json();
          alert(err.error || "Brisanje filtera nije uspjelo");
        }
      } catch (error) {
        console.error("Greška prilikom brisanja filtera:", error);
        alert("Došlo je do greške prilikom brisanja filtera.");
      }
    }
  };

  // Uredi naziv spremljenog filtera - uključi edit mode
  const handleEditFilter = (index) => {
    setEditingFilterIndex(index);
    setEditingFilterName(savedFilters[index].name);
  };

  // Spremi izmijenjeno ime filtera
  const handleSaveEdit = async (index) => {
    if (!editingFilterName || editingFilterName.trim() === "") {
      setEditingFilterIndex(null);
      setEditingFilterName('');
      return;
    }

    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API_BASE_URL}/favorite-filters/${index}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ name: editingFilterName.trim() }),
      });
      if (response.status === 401) {
        handleLogout();
        return;
      }
      if (response.ok) {
        const updated = await response.json();
        if (activeFilterName === savedFilters[index].name) setActiveFilterName(updated.name);
        setSavedFilters(prev => {
          const newFilters = [...prev];
          newFilters[index] = updated;
          return newFilters;
        });
        setEditingFilterIndex(null);
        setEditingFilterName('');
      } else {
        const err = await response.json();
        alert(err.error || "Ažuriranje filtera nije uspjelo");
      }
    } catch (error) {
      console.error("Greška prilikom ažuriranja filtera:", error);
      alert("Došlo je do greške prilikom ažuriranja filtera.");
    }
  };

  // Odustani od uređivanja
  const handleCancelEdit = () => {
    setEditingFilterIndex(null);
    setEditingFilterName('');
  };

  // Add new useEffect for handling clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.mobile-account-wrap')) setShowMobileUserSubmenu(false);
      // Check if click is outside account dropdown
      if (showAccountDropdown && !event.target.closest('.account-button') && !event.target.closest('.account-trigger') && !event.target.closest('.account-dropdown')) {
        setShowAccountDropdown(false);
      }
      // Check if click is outside filters dropdown
      if (showFiltersDropdown &&
          !event.target.closest('.filter-button') &&
          !event.target.closest('.filter-dropdown') &&
          !event.target.closest('.mobile-filter-wrap') &&
          !event.target.closest('.mobile-filter-toggle-tray')) {
        setShowFiltersDropdown(false);
        setShowUpdateConfirmation(false);
      }
      // Check if click is outside mobile hamburger menu
      if (showMobileMenu &&
          !event.target.closest('.mobile-menu') &&
          !event.target.closest('.mobile-dropdown') &&
          !event.target.closest('.filter-dropdown')) {
        setShowMobileMenu(false);
        setShowFiltersDropdown(false);
        setShowUpdateConfirmation(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAccountDropdown, showFiltersDropdown, showMobileMenu]);

  // Login bar uses 'click' (not 'mousedown') so the clicked element's own onClick
  // runs first. Closing the login bar removes a 106px margin-top, shifting the
  // page up — if we close on mousedown, mouseup lands on a different element
  // and the click is lost (forcing the user to click twice).
  // Defer the close with setTimeout so any onClick that just opened the bar
  // (e.g. "Prijavi se" buttons) wins; we re-read showLoginDropdown after batched
  // state updates apply, then close only if it's actually still open.
  const showLoginDropdownRef = useRef(showLoginDropdown);
  useEffect(() => { showLoginDropdownRef.current = showLoginDropdown; }, [showLoginDropdown]);

  useEffect(() => {
    const loginBar = loginBarRef.current;
    if (!loginBar) return undefined;

    const updateHeight = () => setLoginBarHeight(Math.ceil(loginBar.getBoundingClientRect().height));
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(loginBar);
    return () => observer.disconnect();
  }, [showLoginDropdown, loggedIn]);

  useEffect(() => {
    const handleLoginClickOutside = (event) => {
      if (event.target.closest('.login-bar') ||
          event.target.closest('.interest-prompt') ||
          event.target.closest('.login-button') ||
          event.target.closest('.header-login-btn') ||
          event.target.closest('.mobile-login-trigger') ||
          event.target.closest('.login-dropdown') ||
          event.target.closest('.mobile-login-dropdown') ||
          event.target.closest('.mobile-login-overlay')) {
        return;
      }
      if (!showLoginDropdownRef.current) {
        return;
      }
      captureLoginFormData();
      setTimeout(() => {
        if (showLoginDropdownRef.current) {
          setShowLoginDropdown(false);
          setHasUserClosedLoginDropdown(true);
        }
      }, 0);
    };

    document.addEventListener('click', handleLoginClickOutside);
    return () => document.removeEventListener('click', handleLoginClickOutside);
  }, [captureLoginFormData]);

  // Modify handleDropdownClick to toggle the dropdowns
  const handleDropdownClick = (dropdownName) => {
    switch (dropdownName) {
      case 'account':
        setShowAccountDropdown(prev => !prev);
        setShowFiltersDropdown(false);
        setShowLoginDropdown(false);
        break;
      case 'filters':
        setShowFiltersDropdown(prev => !prev);
        setShowUpdateConfirmation(false);
        setShowAccountDropdown(false);
        setShowLoginDropdown(false);
        break;
      case 'login':
        setShowLoginDropdown(prev => !prev);
        setShowAccountDropdown(false);
        setShowFiltersDropdown(false);
        // If user manually opens the dropdown, reset the auto-close preference
        if (!showLoginDropdown) {
          setHasUserClosedLoginDropdown(false);
        }
        break;
      default:
        break;
    }
  };

  // Add new handler for mobile menu
  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  // Add login form handlers
  const handleLoginChange = (e) => {
    capturedLoginFormDataRef.current = null;
    setLoginFormData(prev => {
      const next = { ...prev, [e.target.name]: e.target.value };
      latestLoginFormDataRef.current = next;
      return next;
    });
  };

  const hasAnyLoginCredential = (credentials) =>
    Boolean(credentials.identifier || credentials.password);

  const getCurrentLoginFormData = () =>
    readLoginFormData(capturedLoginFormDataRef.current || latestLoginFormDataRef.current);

  const submitLogin = async (credentials = getCurrentLoginFormData()) => {
    capturedLoginFormDataRef.current = null;
    latestLoginFormDataRef.current = credentials;
    setLoginFormData(credentials);
    setLoginMessage(null);
    setLoginError(null);

    if (!credentials.identifier || !credentials.password) {
      setShowLoginDropdown(true);
      setHasUserClosedLoginDropdown(false);
      setLoginError("Korisničko ime/email i lozinka su obavezni.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok) {
        await clearArticleCacheOnLogin();
        storeCurrentFilter(null);
        setResults([]);
        setOffset(0);
        setHasMore(true);

        // Store token and user data
        localStorage.setItem("token", data.token);
        localStorage.setItem("isAdmin", data.admin);
        
        // Store user data in localStorage
        const userData = {
          username: data.username,
          admin: data.admin,
          current_filter: data.current_filter
        };
        localStorage.setItem("user", JSON.stringify(userData));
        
        setLoginMessage("Prijava uspješna!");
        // Don't set loggedIn to true yet - wait for user/me to succeed
        // This prevents the useEffect from closing the dropdown before we can show errors
        setIsAdmin(data.admin);
        
        // After successful login, fetch user info
        try {
          const userResponse = await fetch(`${API_BASE_URL}/user/me`, {
            headers: { 
              "Authorization": `Bearer ${data.token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          if (!userResponse.ok) {
            throw new Error(`Failed to fetch user data: ${userResponse.status}`);
          }
          
          const userData = await userResponse.json();
          
          // Update stored user data with complete information
          localStorage.setItem("user", JSON.stringify(userData));
          
          storeCurrentFilter(userData.current_filter);
          
          // Set a timeout to refresh the token before it expires (6 days, token expires in 7 days)
          setTimeout(() => {
            refreshToken();
          }, 6 * 24 * 60 * 60 * 1000); // Refresh 6 days after login (token expires in 7 days)
          
          // NOW set loggedIn to true and close dropdown - only after successful user/me fetch
          setUsername(userData.username || data.username || '');
          setLoggedIn(true);
          setShowLoginDropdown(false);
          setLoginFormData({ identifier: "", password: "" });
          await trackEvent('login_success', {
            target: 'login_bar',
            properties: { admin: Boolean(userData.admin) },
          });
          
          // Reload the page to refresh the state
          if (window.jaosveNative) await window.jaosveNative.flushSession();
          window.location.reload();
        } catch (userErr) {
          console.error('Error fetching user data:', userErr);
          // Keep dropdown open so user can see the error message
          // Don't set loggedIn to true, so user can retry
          setLoginError("Prijava uspješna, ali došlo je do greške pri dohvaćanju korisničkih podataka. Molimo pokušajte ponovo ili osvježite stranicu.");
        }
      } else {
        trackEvent('login_failed', {
          target: 'login_bar',
          properties: { status: response.status },
        });
        // Handle different error status codes
        if (response.status === 500) {
          setLoginError("Došlo je do greške na serveru. Molimo pokušajte ponovo za nekoliko trenutaka.");
        } else if (response.status === 401) {
          setLoginError("Pogrešno korisničko ime ili lozinka.");
        } else if (response.status === 429) {
          setLoginError("Previše pokušaja prijave. Molimo pričekajte prije ponovnog pokušaja.");
        } else {
          setLoginError(data.error || data.message || "Prijava nije uspjela. Molimo pokušajte ponovo.");
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setLoginError("Nije moguće povezati se sa serverom. Molimo provjerite svoju internetsku vezu i pokušajte ponovo.");
      } else {
        setLoginError("Došlo je do neočekivane greške. Molimo pokušajte ponovo.");
      }
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    await submitLogin();
  };

  const handleHeaderLoginClick = () => {
    const credentials = getCurrentLoginFormData();

    setShowAccountDropdown(false);
    setShowFiltersDropdown(false);

    if (!showLoginDropdown) {
      setShowLoginDropdown(true);
      setHasUserClosedLoginDropdown(false);
      return;
    }

    if (hasAnyLoginCredential(credentials)) {
      submitLogin(credentials);
      return;
    }

    setShowLoginDropdown(false);
    setHasUserClosedLoginDropdown(true);
  };

  // Function to refresh the token
  const refreshToken = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/refresh-token`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (localStorage.getItem("token") !== token) return;
      if (response.ok) {
        const data = await response.json();
        if (localStorage.getItem("token") !== token) return;
        localStorage.setItem("token", data.token);
        
        // Schedule next refresh (6 days, token expires in 7 days)
        setTimeout(() => {
          refreshToken();
        }, 6 * 24 * 60 * 60 * 1000);
      } else {
        // Only log out if token is actually expired/invalid (401/403)
        // For other errors, keep token - might be server issue
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("isAdmin");
          localStorage.removeItem("currentFilter");
          setLoggedIn(false);
          setIsAdmin(false);
        } else {
          console.warn("Server error refreshing token, but keeping existing token:", response.status);
        }
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      // Only remove token if it's a network error that suggests token is invalid
      // For other errors, keep token - might be server issue
      if (error.message && (error.message.includes('401') || error.message.includes('403'))) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("isAdmin");
        localStorage.removeItem("currentFilter");
        setLoggedIn(false);
        setIsAdmin(false);
      } else {
        console.warn("Network error refreshing token, but keeping existing token");
      }
    }
  };

  // Render active filters display
  const renderActiveFilters = () => {
    const activeFilters = [];
    
    // Add search query if present
    if (query.trim() && !(multiKeywordEnabled && multiKeyword)) {
      activeFilters.push({
        type: 'query',
        label: `Pretraga: "${query}"`,
        value: query
      });
    }
    
    // Add selected categories and group selected subcategories by parent category
    if (selectedTags.length > 0) {
      const addedCategories = new Set();
      const knownCategories = categories && typeof categories === 'object' ? categories : {};

      Object.entries(knownCategories).forEach(([categoryName, subcategories = []]) => {
        if (!Array.isArray(subcategories) || subcategories.length === 0) return;

        const selectedSubcategoryCount = subcategories.filter(sub =>
          hasSubcategoryTag(selectedTags, sub, categoryName)
        ).length;
        const hasFullCategory = hasCategoryTag(selectedTags, categoryName);

        if (hasFullCategory || selectedSubcategoryCount > 0) {
          const allSubcategoriesSelected = subcategories.length > 0 &&
            selectedSubcategoryCount === subcategories.length;

          activeFilters.push({
            type: includeTags ? 'main-category' : 'ignored-category',
            label: hasFullCategory || allSubcategoriesSelected
              ? categoryName
              : `${categoryName} (${selectedSubcategoryCount})`,
            value: categoryName
          });
          addedCategories.add(categoryName);
        }
      });

      selectedTags.forEach(tag => {
        if (!tag || typeof tag !== 'object') return;

        if (tag.type === 'category' && !addedCategories.has(tag.name) && !isKnownEmptyCategory(knownCategories, tag.name)) {
          activeFilters.push({
            type: includeTags ? 'main-category' : 'ignored-category',
            label: tag.name,
            value: tag.name
          });
          addedCategories.add(tag.name);
        }

        if (tag.type === 'subcategory' && tag.parent && !addedCategories.has(tag.parent) && !isKnownEmptyCategory(knownCategories, tag.parent)) {
          const selectedSubcategoryCount = selectedTags.filter(item =>
            item && typeof item === 'object' && item.type === 'subcategory' && item.parent === tag.parent
          ).length;

          activeFilters.push({
            type: includeTags ? 'main-category' : 'ignored-category',
            label: `${tag.parent} (${selectedSubcategoryCount})`,
            value: tag.parent
          });
          addedCategories.add(tag.parent);
        }
      });
    }
    
    // Add selected portals
    if (selectedPortals.length > 0) {
      selectedPortals.forEach(portal => {
        activeFilters.push({
          type: includePortals ? 'portal' : 'ignored-portal',
          label: portal,
          value: portal
        });
      });
    }
    
    // Add date filters
    if (fromDate) {
      activeFilters.push({
        type: 'date',
        label: `Od: ${fromDate.format('DD/MM/YYYY HH:mm')}`,
        value: fromDate.format('YYYY-MM-DDTHH:mm')
      });
    }
    
    if (toDate) {
      activeFilters.push({
        type: 'date',
        label: `Do: ${toDate.format('DD/MM/YYYY HH:mm')}`,
        value: toDate.format('YYYY-MM-DDTHH:mm')
      });
    }

    if (activeFilters.length === 0) {
      return null;
    }
    
    return (
      <div className="active-filters-container">
        <div className="active-filters-heading">
          <span>Aktivni filteri <strong>{activeFilters.length}</strong></span>
          <button
            type="button"
            className="active-filters-clear-btn"
            onClick={handleRequestClearAllFilters}
            aria-label="Isključi sve oznake"
            title="Isključi sve oznake"
          >
            <ControlIcon name="trash" /> Očisti
          </button>
        </div>
        <div className="active-filters-list">
          {activeFilters.map((filter, index) => (
            <span key={index} className={`active-filter-tag ${filter.type}`}>
              {filter.label}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Prebacivanje teme
  const handleToggleTheme = async () => {
    const newTheme = !darkTheme;
    trackEvent('theme_toggle', {
      target: 'theme',
      properties: { theme: newTheme ? 'dark' : 'light' },
    });
    setDarkTheme(newTheme);
    
    // Save theme preference to database if user is logged in
    if (loggedIn) {
      try {
        const token = localStorage.getItem("token");
        await fetch(`${API_BASE_URL}/update-theme`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ theme: newTheme })
        });
      } catch (error) {
        console.error("Error saving theme preference:", error);
      }
    }
  };

  const handleCommercialBannerClick = () => {
    if (!commercialBannerLink) return;

    trackEvent('banner_click', {
      target: commercialBannerLink,
      properties: {
        banner_url: commercialBanner,
      },
    });
    window.open(commercialBannerLink, '_blank', 'noopener,noreferrer');
  };

  const handleArticleOpen = (article) => {
    trackEvent('article_open', {
      target: article.link,
      properties: {
        article_id: article.id,
        article_title: article.title,
        article_link: article.link,
        portal: getPortalName(article.source),
        tag: article.tag,
        publish_date: article.publish_date,
      },
    });
  };

  return (
    <div
      className={`search-container ${darkTheme ? 'dark-theme' : 'light-theme'}${!loggedIn && showLoginDropdown ? ' login-bar-open' : ''}`}
      style={{ '--mobile-login-bar-bottom': `${50 + loginBarHeight}px`, '--login-bar-height': `${loginBarHeight}px` }}
    >
      {/* Loading overlay — disappears when structural data loads, before articles */}
      {!uiReady && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p className="loading-text">UČITAVANJE</p>
          </div>
        </div>
      )}
      <div className="logo-tray">
        <div
          className="logo-container"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{ cursor: 'pointer' }}
        >
          <div className="tech-logo">
            <img
              src={darkTheme ? '/logo-dark.png' : '/logo-light.png'}
              alt="JaoSVE!"
              className="logo-image"
            />
          </div>
        </div>
        <div className="logo-space"></div>

        {/* Mobile tray actions - centered theme toggle + filter icon */}
        <div className="mobile-tray-actions">
          <button
            onClick={handleToggleTheme}
            className="mobile-tray-btn mobile-theme-toggle-tray"
            aria-label={darkTheme ? "Svijetla tema" : "Tamna tema"}
          >
            {darkTheme ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>
          {loggedIn && (
            <div className="mobile-filter-wrap">
              <button
                onClick={() => handleDropdownClick('filters')}
                className="mobile-tray-btn mobile-filter-toggle-tray"
                aria-label="Moji filteri"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              </button>
              {showFiltersDropdown && (
                <div className="filter-dropdown mobile-filter-dropdown">
                  {isCreatingNewFilter && (
                    <div className="new-filter-form">
                      <div className="new-filter-label">Novi filter</div>
                      <input
                        type="text"
                        value={newFilterName}
                        onChange={(e) => setNewFilterName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') confirmSaveNewFilter();
                          else if (e.key === 'Escape') cancelNewFilter();
                        }}
                        className="filter-name-input"
                        placeholder="Ime filtera"
                        autoFocus
                      />
                      <div className="filter-actions">
                        <button onClick={confirmSaveNewFilter} className="save-btn">Spremi</button>
                        <button onClick={cancelNewFilter} className="cancel-btn">Odustani</button>
                      </div>
                    </div>
                  )}
                  <div className="saved-filters">
                    <h4>Spašeni filteri</h4>
                    {isLoadingFilters ? (
                      <p>Učitavanje filtera...</p>
                    ) : savedFilters.length === 0 ? (
                      <p>Nema spremljenih filtera</p>
                    ) : (
                      [...savedFilters].map((filter, index) => ({ filter, index })).reverse().map(({ filter, index }) => (
                        <div key={filter.id || index} className={`saved-filter-item${filter.name === activeFilterName ? ' active' : ''}`}>
                          {editingFilterIndex === index ? (
                            <div className="filter-edit-mode">
                              <input
                                type="text"
                                value={editingFilterName}
                                onChange={(e) => setEditingFilterName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveEdit(index);
                                  } else if (e.key === 'Escape') {
                                    handleCancelEdit();
                                  }
                                }}
                                className="filter-name-input"
                                autoFocus
                              />
                              <div className="filter-actions">
                                <button onClick={() => handleSaveEdit(index)} className="save-btn">Spremi</button>
                                <button onClick={handleCancelEdit} className="cancel-btn">Odustani</button>
                              </div>
                            </div>
                          ) : (
                            <>
                                <SavedFilterHeader filter={filter} onChanged={refreshNotificationSubscriptions}
                                  subscription={notificationSubscriptions.find(row => row.saved_filter_id === filter.id || (!row.saved_filter_id && row.name === filter.name))} />
                              <div className="filter-actions">
                                <button onClick={() => applySavedFilter(filter)} className="apply-btn"><ControlIcon name="check" />Primijeni</button>
                                <button onClick={() => handleEditFilter(index)} className="edit-btn"><ControlIcon name="edit" />Uredi</button>
                                <button onClick={() => handleDeleteFilter(index)} className="delete-btn"><ControlIcon name="trash" />Obriši</button>
                              </div>
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="logo-space logo-space-right"></div>

        <div className="user-settings-placeholder">
          {/* Desktop buttons */}
          <div className="desktop-buttons">
            <div className="nav-tabs">
            {/* Početna button - always visible, active on Search page */}
            <div className="nav-tab-item">
              <button
                onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="filter-button active"
              >
                Početna
              </button>
            </div>

            {/* O nama button - always visible */}
            <div className="nav-tab-item">
              <button
                onClick={() => navigate("/about")}
                className="filter-button"
              >
                O nama
              </button>
            </div>

            <div className="nav-tab-item">
              <button
                onClick={() => navigate("/how-to-use")}
                className="filter-button"
              >
                Kako koristiti
              </button>
            </div>

            {loggedIn ? (
              <>
                {isAdmin && (
                  <div className="nav-tab-item">
                    <button
                      onClick={() => navigate("/admin")}
                      className="filter-button"
                    >
                      Admin Panel
                    </button>
                  </div>
                )}
                <div className="nav-tab-item">
                  <button
                    onClick={() => handleDropdownClick('filters')}
                    aria-expanded={showFiltersDropdown}
                    className="filter-button saved-filters-trigger"
                  >
                    Moji filteri
                  </button>
                  {showFiltersDropdown && (
                    <div className="filter-dropdown">
                      {isCreatingNewFilter && (
                        <div className="new-filter-form">
                          <div className="new-filter-label">Novi filter</div>
                          <input
                            type="text"
                            value={newFilterName}
                            onChange={(e) => setNewFilterName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') confirmSaveNewFilter();
                              else if (e.key === 'Escape') cancelNewFilter();
                            }}
                            className="filter-name-input"
                            placeholder="Ime filtera"
                            autoFocus
                          />
                          <div className="filter-actions">
                            <button onClick={confirmSaveNewFilter} className="save-btn">Spremi</button>
                            <button onClick={cancelNewFilter} className="cancel-btn">Odustani</button>
                          </div>
                        </div>
                      )}
                      <div className="saved-filters">
                        <h4>Spašeni filteri</h4>
                        {isLoadingFilters ? (
                          <p>Učitavanje filtera...</p>
                        ) : savedFilters.length === 0 ? (
                          <p>Nema spremljenih filtera</p>
                        ) : (
                          [...savedFilters].map((filter, index) => ({ filter, index })).reverse().map(({ filter, index }) => (
                            <div key={filter.id || index} className={`saved-filter-item${filter.name === activeFilterName ? ' active' : ''}`}>
                              {editingFilterIndex === index ? (
                                <div className="filter-edit-mode">
                                  <input
                                    type="text"
                                    value={editingFilterName}
                                    onChange={(e) => setEditingFilterName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveEdit(index);
                                      } else if (e.key === 'Escape') {
                                        handleCancelEdit();
                                      }
                                    }}
                                    className="filter-name-input"
                                    autoFocus
                                  />
                                  <div className="filter-actions">
                                    <button
                                      onClick={() => handleSaveEdit(index)}
                                      className="save-btn"
                                    >
                                      Spremi
                                    </button>
                                    <button
                                      onClick={handleCancelEdit}
                                      className="cancel-btn"
                                    >
                                      Odustani
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                    <SavedFilterHeader filter={filter} onChanged={refreshNotificationSubscriptions}
                                      subscription={notificationSubscriptions.find(row => row.saved_filter_id === filter.id || (!row.saved_filter_id && row.name === filter.name))} />
                                  <div className="filter-actions">
                                    <button
                                      onClick={() => applySavedFilter(filter)}
                                      className="apply-btn"
                                    >
                                      <ControlIcon name="check" /> Primijeni
                                    </button>
                                    <button
                                      onClick={() => handleEditFilter(index)}
                                      className="edit-btn"
                                    >
                                      <ControlIcon name="edit" /> Uredi
                                    </button>
                                    <button
                                      onClick={() => handleDeleteFilter(index)}
                                      className="delete-btn"
                                    >
                                      <ControlIcon name="trash" /> Obriši
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <ThemeToggle darkTheme={darkTheme} onClick={handleToggleTheme} />
                <div className="nav-tab-item">
                  <button
                    onClick={() => handleDropdownClick('account')}
                    className="filter-button account-trigger"
                    title="Vaše preference"
                    aria-label={`Korisnički meni: ${username}`} aria-expanded={showAccountDropdown}
                  >
                    <AccountAvatar username={username} />
                  </button>
                  {showAccountDropdown && (
                    <div className="account-dropdown">
                      <button onClick={() => navigate("/postavke")}>
                        Postavke
                      </button>
                      <button onClick={handleLogout}>
                        Odjavi se
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : null}
            </div>

            {!loggedIn && (
              <>
                <ThemeToggle darkTheme={darkTheme} onClick={handleToggleTheme} />
                <button
                  onClick={() => navigate("/signup")}
                  className="filter-button"
                >
                  Registruj se
                </button>
                <button
                  onMouseDown={captureLoginFormData}
                  onClick={handleHeaderLoginClick}
                  className="header-login-btn"
                >
                  Prijavi se
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger menu */}
          <div className="mobile-menu">
            {loggedIn && (
              <div className="mobile-account-wrap">
                <button className="mobile-account-trigger" onClick={() => { setShowMobileUserSubmenu((v) => !v); setShowMobileMenu(false); setShowFiltersDropdown(false); setShowAccountDropdown(false); }} aria-label={`Korisnički meni na manjim ekranima: ${username}`} aria-expanded={showMobileUserSubmenu}>
                  <AccountAvatar username={username} />
                </button>
                {showMobileUserSubmenu && (
                  <div className="account-dropdown mobile-account-dropdown">
                    <button onClick={() => { setShowMobileUserSubmenu(false); navigate('/postavke'); }}>Postavke</button>
                    <button onClick={handleLogout}>Odjavi se</button>
                  </div>
                )}
              </div>
            )}
            <button className="hamburger-button" onClick={() => { toggleMobileMenu(); setShowMobileUserSubmenu(false); setShowFiltersDropdown(false); setShowAccountDropdown(false); }} aria-label="Meni" aria-expanded={showMobileMenu}>
              <span className={`hamburger-icon${showMobileMenu ? ' open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
            {showMobileMenu && (
              <div className="mobile-dropdown">
                {/* Početna button - always visible, active on Search page */}
                <button
                  className="active"
                  onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: 'smooth' }); setShowMobileMenu(false); }}
                >
                  Početna
                </button>
                {/* O nama button - always visible */}
                <button onClick={() => navigate("/about")}>
                  O nama
                </button>
                <button onClick={() => navigate("/how-to-use")}>
                  Kako koristiti
                </button>
                
                {loggedIn ? (
                  <>
                    {isAdmin && (
                      <button onClick={() => navigate("/admin")}>
                        Admin Panel
                      </button>
                    )}
                    <button onClick={() => handleDropdownClick('filters')}>
                      Moji filteri
                    </button>
                    {showFiltersDropdown && (
                      <div className="filter-dropdown">
                        {isCreatingNewFilter && (
                          <div className="new-filter-form">
                            <div className="new-filter-label">Novi filter</div>
                            <input
                              type="text"
                              value={newFilterName}
                              onChange={(e) => setNewFilterName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') confirmSaveNewFilter();
                                else if (e.key === 'Escape') cancelNewFilter();
                              }}
                              className="filter-name-input"
                              placeholder="Ime filtera"
                              autoFocus
                            />
                            <div className="filter-actions">
                              <button onClick={confirmSaveNewFilter} className="save-btn">Spremi</button>
                              <button onClick={cancelNewFilter} className="cancel-btn">Odustani</button>
                            </div>
                          </div>
                        )}
                        <div className="saved-filters">
                          <h4>Spašeni filteri</h4>
                          {isLoadingFilters ? (
                            <p>Učitavanje filtera...</p>
                          ) : savedFilters.length === 0 ? (
                            <p>Nema spremljenih filtera</p>
                          ) : (
                            [...savedFilters].map((filter, index) => ({ filter, index })).reverse().map(({ filter, index }) => (
                              <div key={filter.id || index} className={`saved-filter-item${filter.name === activeFilterName ? ' active' : ''}`}>
                                {editingFilterIndex === index ? (
                                  <div className="filter-edit-mode">
                                    <input
                                      type="text"
                                      value={editingFilterName}
                                      onChange={(e) => setEditingFilterName(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handleSaveEdit(index);
                                        } else if (e.key === 'Escape') {
                                          handleCancelEdit();
                                        }
                                      }}
                                      className="filter-name-input"
                                      autoFocus
                                    />
                                    <div className="filter-actions">
                                      <button onClick={() => handleSaveEdit(index)} className="save-btn">Spremi</button>
                                      <button onClick={handleCancelEdit} className="cancel-btn">Odustani</button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                      <SavedFilterHeader filter={filter} onChanged={refreshNotificationSubscriptions}
                                        subscription={notificationSubscriptions.find(row => row.saved_filter_id === filter.id || (!row.saved_filter_id && row.name === filter.name))} />
                                    <div className="filter-actions">
                                      <button onClick={() => applySavedFilter(filter)} className="apply-btn"><ControlIcon name="check" />Primijeni</button>
                                      <button onClick={() => handleEditFilter(index)} className="edit-btn"><ControlIcon name="edit" />Uredi</button>
                                      <button onClick={() => handleDeleteFilter(index)} className="delete-btn"><ControlIcon name="trash" />Obriši</button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                    </>
                ) : (
                  <>
                    <button onClick={() => navigate("/signup")}>
                      Registruj se
                    </button>
                    <button
                      className="mobile-login-trigger"
                      onMouseDown={captureLoginFormData}
                      onClick={() => { handleHeaderLoginClick(); setShowMobileMenu(false); }}
                    >
                      Prijavi se
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Login Bar — below logo-tray */}
      {!loggedIn && showLoginDropdown && (
        <div className="login-bar" ref={loginBarRef}>
          {loginMessage && <span className="login-bar-alert success">{loginMessage}</span>}
          {loginError && <span className="login-bar-alert danger">{loginError}</span>}
          <form onSubmit={handleLoginSubmit} className="login-bar-form" noValidate>
            <input
              ref={loginIdentifierInputRef}
              type="text"
              name="identifier"
              value={loginFormData.identifier}
              onChange={handleLoginChange}
              onInput={handleLoginChange}
              required
              className="login-bar-input"
              placeholder="Korisničko ime ili email"
              inputMode="email"
              autoComplete="username"
            />
            <div className="login-bar-pass-wrap">
              <input
                ref={loginPasswordInputRef}
                type={showLoginPassword ? "text" : "password"}
                name="password"
                value={loginFormData.password}
                onChange={handleLoginChange}
                onInput={handleLoginChange}
                required
                className="login-bar-input"
                placeholder="Lozinka"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="login-bar-pass-toggle"
                onClick={() => setShowLoginPassword(!showLoginPassword)}
                tabIndex={-1}
              >
                {showLoginPassword ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            <button type="submit" className="login-bar-submit" onMouseDown={captureLoginFormData}>
              Prijavi se
            </button>
          </form>
          <div className="login-bar-right">
            <a href="/forgot-password" className="login-bar-forgot">
              Zaboravili ste lozinku?
            </a>
            <button
              className="login-bar-close"
              onClick={() => {
                captureLoginFormData();
                setShowLoginDropdown(false);
                setHasUserClosedLoginDropdown(true);
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Maintenance mode overlay - visible to non-admin users */}
      {maintenanceMode && !isAdmin && (
        <div className="maintenance-overlay">
          <div className="maintenance-content">
            <div className="maintenance-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
            </div>
            <h2 className="maintenance-title">Radovi u toku</h2>
            <p className="maintenance-message">Hvala na strpljenju! Stranica je trenutno u fazi održavanja. Molimo pokušajte ponovo kasnije.</p>
          </div>
        </div>
      )}

      {(!maintenanceMode || isAdmin) && allDataLoaded && (
      <div className="search-container">
        {commercialBanner && (
          <div
            className="commercial-banner"
            style={{
              width: '100%',
              height: commercialDimensions.displayHeight ? `${commercialDimensions.displayHeight}px` : '120px',
              maxWidth: '100%',
              maxHeight: '400px',
              cursor: commercialBannerLink ? 'pointer' : 'default'
            }}
            onClick={handleCommercialBannerClick}
            ref={(el) => {
              // Banner element reference
            }}
          >
            <BannerImage
              key={commercialBanners[activeBannerIndex]?.url || commercialBanner}
              className={commercialBanners.length > 1 ? 'commercial-banner-fade' : ''}
              src={assetUrl(commercialBanners[activeBannerIndex]?.url || commercialBanner)}
              alt="Banner"
              fetchPriority="high"
              loading="eager"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center'
              }}
              onLoad={() => setLoadingSequence(prev => ({ ...prev, commercialBannerLoaded: true }))}
            />
          </div>
        )}
        <div className="mobile-sticky-controls" ref={mobileStickyRef}>
        <div className="search-filter-row">
          <form className="search-bar" onSubmit={(e) => { e.preventDefault(); submitSearch(); }}>
            <KeywordSearchInput ref={keywordInputRef} query={query} onChange={setQuery} enabled={multiKeywordEnabled && multiKeyword} />
            <CompactSearchOptions multiKeywordEnabled={multiKeywordEnabled} multiKeyword={multiKeyword}
              setMultiKeyword={setMultiKeyword} keywordMode={keywordMode} setKeywordMode={setKeywordMode}
              searchAllEnabled={searchAllEnabled} searchAll={searchAll} setSearchAll={setSearchAll} />
            <button
              type="button"
              className={`search-btn${searchHighlight || searchHasPendingQuery ? ' search-btn-highlight' : ''}`}
              onClick={() => submitSearch()}
              disabled={isSearching}
              aria-label={isSearching ? 'Pretražujem…' : 'Pretraži'}
              aria-busy={isSearching}
              title={searchHighlight || searchHasPendingQuery ? 'Primijeni izmjene — pokreni pretragu' : 'Pretraži — pokreni pretragu'}
            >
              <ControlIcon name="search" />
            </button>
            <button type="button" className="toggle-filters-btn search-filter-toggle" onClick={toggleFilters} aria-expanded={showFilters} aria-controls="news-filters">
              <ControlIcon name="filters" /><span>Filteri</span>
            </button>
          </form>
        </div>
        </div>
        {renderActiveFilters()}
        {showFilters && (
          <div className="filters grouped-filters" id="news-filters" ref={filtersRef}>
            <div className="filters-topbar">
              <p className="filter-search-hint">
                Odaberite filtere pa kliknite <strong>Prikaži vijesti</strong> pri dnu ekrana ili dugme s lupom.
              </p>
              <button
                type="button"
                className="clear-all-filters-btn"
                onClick={handleRequestClearAllFilters}
                disabled={!hasActiveFilters}
                aria-label="Obriši sve filtere"
                title="Obriši sve filtere"
              >
                <ControlIcon name="reset" /><span>Očisti sve</span>
              </button>
            </div>
            <div className="filter-action-buttons grouped-filter-actions" role="group" aria-label="Spremanje i dijeljenje filtera">
            <button
              type="button"
              className="save-filter-btn"
              onClick={() => {
                if (!loggedIn) {
                  alert("Morate biti prijavljeni da biste spremili filtere.");
                  return;
                }
                handleSaveCurrentFilter();
              }}
            >
              <ControlIcon name="plus" /> Novi filter
            </button>
            <button
              type="button"
              className="update-filter-btn"
              onClick={() => {
                if (!loggedIn) {
                  alert("Morate biti prijavljeni da biste ažurirali filtere.");
                  return;
                }
                handleUpdateCurrentFilter();
              }}
            >
              <ControlIcon name="save" /> Ažuriraj filter
            </button>
            <button
              type="button"
              className="share-filter-btn"
              onClick={openShareDialog}
              disabled={!hasActiveFilters}
              aria-label="Podijeli trenutni filter"
              title={hasActiveFilters ? 'Podijeli trenutni filter' : 'Odaberi filtere za dijeljenje'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg><span>Podijeli</span>
            </button>
            </div>
            <SearchOptionsHelp multiKeywordEnabled={multiKeywordEnabled} searchAllEnabled={searchAllEnabled} />
            <FilterGroups groups={[
              { key: 'categories', label: 'Kategorije', icon: 'categories', count: new Set(selectedTags.filter(Boolean).map(tag => typeof tag === 'string' ? tag : tag.parent || tag.name)).size, content: <>
            <div className="filter-section-header">
              <span className="filter-section-title">Kategorije</span>
              <div className="filter-section-actions">
                <label className="include-exclude-checkbox">
                  <ControlIcon name="check" /> Uvrsti
                  <input
                    type="checkbox"
                    checked={includeTags}
                    onChange={() => { clearQuickTopic(); setIncludeTags(true); }}
                  />
                </label>
                <label className="include-exclude-checkbox ignore">
                  <ControlIcon name="minus" /> Ignoriši
                  <input
                    type="checkbox"
                    checked={!includeTags}
                    onChange={() => { clearQuickTopic(); setIncludeTags(false); }}
                  />
                </label>
                <button type="button" className="clear-filters-btn" onClick={handleClearTags}>
                  Očisti kategorije
                </button>
              </div>
            </div>
            {renderTagGroups()}
              </> },
              { key: 'portals', label: 'Portali', icon: 'portals', count: selectedPortals.length, content: <>
            <div className="filter-section-header">
              <span className="filter-section-title">Portali</span>
              <div className="filter-section-actions">
                <label className="include-exclude-checkbox">
                  <ControlIcon name="check" /> Uvrsti
                  <input
                    type="checkbox"
                    checked={includePortals}
                    onChange={() => setIncludePortals(true)}
                  />
                </label>
                <label className="include-exclude-checkbox ignore">
                  <ControlIcon name="minus" /> Ignoriši
                  <input
                    type="checkbox"
                    checked={!includePortals}
                    onChange={() => setIncludePortals(false)}
                  />
                </label>
                <button type="button" className="clear-filters-btn" onClick={handleClearPortals}>
                  Očisti portale
                </button>
              </div>
            </div>
            <div className={`portal-list ${!includePortals ? 'red-checkbox' : ''}`}>
              {portals.map((portal, index) => (
                <label key={index} className="portal-checkbox">
                  <input
                    type="checkbox"
                    value={portal.name}
                    onChange={() => handlePortalChange(portal.name)}
                    checked={selectedPortals.includes(portal.name)}
                  />
                  {portal.name}
                </label>
              ))}
            </div>
              </> },
              { key: 'countries', label: 'Države', icon: 'globe', count: selectedCountries.length, content: <>
            <div className="filter-section-header">
              <span className="filter-section-title">Države</span>
            </div>
            <div className={`country-list ${!includePortals ? 'red-checkbox' : ''}`}>
              {countries.map((country, index) => (
                <label key={index} className="country-checkbox">
                  <input
                    type="checkbox"
                    value={country}
                    onChange={() => handleCountryChange(country)}
                    checked={selectedCountries.includes(country)}
                  />
                  {country}
                </label>
              ))}
            </div>
              </> },
              { key: 'time', label: 'Vrijeme', icon: 'clock', count: Number(Boolean(fromDate)) + Number(Boolean(toDate)), content: <>
                <div className="date-filters grouped-date-filters">
                  <div className="datetime-row">
                    <label>Od:</label>
                    <Datetime
                      value={fromDate}
                      onChange={(date) => {
                        if (moment.isMoment(date)) setFromDate(date);
                        else if (typeof date === 'string' && date === '') setFromDate(null);
                      }}
                      locale="bs"
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                      renderInput={(props, openCalendar) => (
                        <div className="datetime-input-container">
                          <input
                            {...props}
                            placeholder="Odaberi datum"
                            onFocus={() => setTimeInputFocused(true)}
                            onBlur={() => {
                              setTimeout(() => {
                                if (!document.activeElement.closest('.datetime-input-container')) {
                                  setTimeInputFocused(false);
                                }
                              }, 100);
                            }}
                          />
                          <div className="time-picker-container">
                            <input
                              type="time"
                              className="time-picker"
                              onFocus={() => setTimeInputFocused(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  if (!document.activeElement.closest('.time-picker-container')) {
                                    setTimeInputFocused(false);
                                  }
                                }, 100);
                              }}
                              onChange={(e) => {
                                const [hours, minutes] = e.target.value.split(':');
                                const newDate = (fromDate && moment.isMoment(fromDate)) ? moment(fromDate) : moment();
                                newDate.hours(parseInt(hours));
                                newDate.minutes(parseInt(minutes));
                                setFromDate(newDate);
                              }}
                              value={fromDate && moment.isMoment(fromDate) ? fromDate.format('HH:mm') : ''}
                            />
                          </div>
                        </div>
                      )}
                      closeOnSelect={true}
                    />
                  </div>
                  <div className="datetime-row">
                    <label>Do:</label>
                    <Datetime
                      value={toDate}
                      onChange={(date) => {
                        if (moment.isMoment(date)) setToDate(date);
                        else if (typeof date === 'string' && date === '') setToDate(null);
                      }}
                      locale="bs"
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                      renderInput={(props, openCalendar) => (
                        <div className="datetime-input-container">
                          <input
                            {...props}
                            placeholder="Odaberi datum"
                            onFocus={() => setTimeInputFocused(true)}
                            onBlur={() => {
                              setTimeout(() => {
                                if (!document.activeElement.closest('.datetime-input-container')) {
                                  setTimeInputFocused(false);
                                }
                              }, 100);
                            }}
                          />
                          <div className="time-picker-container">
                            <input
                              type="time"
                              className="time-picker"
                              onFocus={() => setTimeInputFocused(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  if (!document.activeElement.closest('.time-picker-container')) {
                                    setTimeInputFocused(false);
                                  }
                                }, 100);
                              }}
                              onChange={(e) => {
                                const [hours, minutes] = e.target.value.split(':');
                                const newDate = (toDate && moment.isMoment(toDate)) ? moment(toDate) : moment();
                                newDate.hours(parseInt(hours));
                                newDate.minutes(parseInt(minutes));
                                setToDate(newDate);
                              }}
                              value={toDate && moment.isMoment(toDate) ? toDate.format('HH:mm') : ''}
                            />
                          </div>
                        </div>
                      )}
                      closeOnSelect={true}
                    />
                  </div>
                  <button
                    type="button"
                    className="clear-btn"
                    onClick={() => {
                      setFromDate(null);
                      setToDate(null);
                    }}
                    style={{ alignSelf: 'flex-start', marginTop: '4px' }}
                  >
                    Očisti vrijeme
                  </button>

                </div>
              </> }
            ]} />
            <div className="filter-apply-bar">
              <div className="filter-apply-summary">
                <span>Kategorije: {new Set(selectedTags.filter(Boolean).map(tag => typeof tag === 'string' ? tag : tag.parent || tag.name)).size} · Portali: {selectedPortals.length}</span>
                <span className="filter-apply-status" role="status">
                  {isSearching ? 'Pretražujem…' : searchHighlight || searchHasPendingQuery ? 'Izmjene nisu primijenjene' : 'Izbor je primijenjen'}
                </span>
              </div>
              <button type="button" className={`filter-apply-btn${searchHighlight || searchHasPendingQuery ? ' search-btn-highlight' : ''}`} disabled={isSearching} aria-busy={isSearching}
                onClick={() => submitSearch('filters_panel')}>
                <ControlIcon name="search" /><span>{isSearching ? 'Pretražujem…' : 'Prikaži vijesti'}</span>
              </button>
            </div>
          </div>
        )}
        {showClearAllConfirmation && (
          <div
            className="clear-all-confirm-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="clear-all-confirm-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowClearAllConfirmation(false);
              }
            }}
          >
            <div className="clear-all-confirm-modal">
              <h3 id="clear-all-confirm-title">Isključiti sve oznake?</h3>
              <p>Ovo će obrisati sve aktivne kategorije, portale, države, vrijeme i pretragu.</p>
              <div className="clear-all-confirm-actions">
                <button
                  type="button"
                  className="clear-all-cancel-btn"
                  onClick={() => setShowClearAllConfirmation(false)}
                >
                  Odustani
                </button>
                <button
                  type="button"
                  className="clear-all-confirm-btn"
                  onClick={handleConfirmClearAllFilters}
                >
                  Isključi sve
                </button>
              </div>
            </div>
          </div>
        )}
        {showShareDialog && (
          <div
            className="share-filter-overlay"
            role="presentation"
            onClick={(event) => {
              if (event.target === event.currentTarget) setShowShareDialog(false);
            }}
          >
            <div
              className="share-filter-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="share-filter-title"
            >
              <div className="share-filter-header">
                <h3 id="share-filter-title">Podijeli feed</h3>
                <button
                  type="button"
                  className="share-filter-close"
                  onClick={() => setShowShareDialog(false)}
                  aria-label="Zatvori dijeljenje"
                  title="Zatvori"
                >
                  ×
                </button>
              </div>
              <div className="share-filter-dialog-actions">
                <button type="button" className="share-copy-btn" onClick={copyShareLink}>
                  Kopiraj link
                </button>
                {typeof navigator.share === 'function' && (
                  <button type="button" className="share-native-btn" onClick={shareFilterNatively}>
                    Podijeli
                  </button>
                )}
              </div>
              {shareFeedback && (
                <p className="share-filter-feedback" role="status">{shareFeedback}</p>
              )}
            </div>
          </div>
        )}
        <div className="results-toolbar"><h2 ref={resultsHeadingRef} tabIndex={-1}>Vijesti</h2><ArticleViewPicker view={articleView} onChange={setArticleView} /></div>
        <div className="results" aria-busy={isSearching}>
          {filteredResults.length > 0 ? (
            <ArticleGrid view={articleView}>
              {filteredResults.map((article, index) => (
                      <a
                        key={article.link || index}
                        className={`article-card${!getProxiedImageUrl(article.image) ? ' no-image' : ''}`}
                        href={article.link && typeof article.link === 'string' ? article.link : '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleArticleOpen(article)}
                        ref={index === filteredResults.length - 1 ? observeLastArticle : null}
                      >
                        <ArticleImage src={getProxiedImageUrl(article.image)} loading={index < 8 ? "eager" : "lazy"} />
                        <div className="article-body">
                          <div className="article-meta">
                            <span className="article-source">{getPortalName(article.source)}</span>
                            <span className="article-meta-dot"></span>
                            <span className="article-date">{article.publish_date}</span>
                            {showSubcategoryOnArticles && article.tag && (
                              <>
                                <span className="article-meta-dot"></span>
                                <span className="article-subcategory">{article.tag}</span>
                              </>
                            )}
                          </div>
                          <h3 className="article-title">{article.title}</h3>
                        </div>
                      </a>
              ))}
            </ArticleGrid>
          ) : (
            !isLoading && !isSearching && !isInitialLoad ? (
              <p>Nema rezultata</p>
            ) : (
              <p>{isSearching ? 'Pretražujem…' : 'Učitavanje…'}</p>
            )
          )}
        </div>
        {isLoading && results.length > 0 && <p>Učitavanje...</p>}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
      </div>
      )}

      {showInterestPrompt && !loggedIn && (
        <aside
          className="interest-prompt"
          role="dialog"
          aria-modal="false"
          aria-labelledby="interest-prompt-title"
          onClick={event => event.stopPropagation()}
        >
          <div className="interest-prompt-header">
            <div>
              <h2 id="interest-prompt-title">Šta želiš čitati danas?</h2>
            </div>
            <button
              type="button"
              className="interest-prompt-close"
              onClick={event => {
                event.stopPropagation();
                dismissInterestPrompt('close_button');
              }}
              aria-label="Zatvori izbor tema"
              title="Zatvori"
            >
              ×
            </button>
          </div>
          <div className="interest-prompt-options">
            {Object.keys(categories)
              .sort((first, second) => first.localeCompare(second, 'bs'))
              .map(categoryName => (
              <button
                key={categoryName}
                type="button"
                onClick={() => handleInterestChoice(categoryName)}
              >
                {categoryName}
              </button>
            ))}
          </div>
        </aside>
      )}

      {showScrollTop && (
        <button
          className="scroll-to-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 4L4 12H16L10 4Z" fill="currentColor"/>
          </svg>
        </button>
      )}

    </div>
  );
}

export default Search;
